<template>
  <div class="lock-desk-queue-page">
    <template v-if="isAuthorized">
      <div class="page-header">
        <div class="page-header-left">
          <div class="page-icon">
            <i class="fa-duotone fa-inbox-full"></i>
          </div>
          <div>
            <h1 class="page-title">Lock Desk Queue</h1>
            <p class="page-subtitle">Intake tracking for lock desk requests</p>
          </div>
        </div>

        <div class="header-actions">
          <select v-model="timeFrame" class="filter-select days-select">
            <option value="all">All</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="last_7_days">Last 7 Days</option> 
            <option value="last_30_days">Last 30 Days</option>
            <option value="last_90_days">Last 90 Days</option>
          </select>

          <button class="refresh-btn" @click="loadData" :disabled="loading">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>

          <button class="insights-btn"
                  @click="openInsights"
                  title="Queue Insights">
            <i class="fa-duotone fa-chart-mixed"></i>
          </button>

          <button v-if="isLockDeskManager"
                  class="approvers-btn"
                  @click="openApproverImport"
                  title="Concession Approvers">
            <i class="fa-duotone fa-file-import"></i>
          </button>

          <button class="add-btn" @click="openCreateModal" title="New Request">
            <i class="fa-duotone fa-plus"></i>
          </button>
        </div>
      </div>

      <div class="summary-sections">
        <div class="summary-section">
          <div class="section-header">
            <div class="section-title-wrap">
              <i class="fa-duotone fa-list-check section-icon"></i>
              <span class="section-title">Queue Overview</span>
            </div>
          </div>
          <div class="section-cards overview-cards">
            <div class="summary-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-table-rows summary-icon"></i>
                <span class="summary-label">Total</span>
              </div>
              <div class="summary-value">{{ summary.total }}</div>
            </div>
            <div class="summary-card not-started-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-circle-dashed summary-icon"></i>
                <span class="summary-label">Not Started</span>
              </div>
              <div class="summary-value">{{ summary.not_started }}</div>
            </div>
            <div class="summary-card in-progress-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-spinner summary-icon"></i>
                <span class="summary-label">In Progress</span>
              </div>
              <div class="summary-value">{{ summary.in_progress }}</div>
            </div>
            <div class="summary-card done-card">
              <div class="summary-card-header">
                <i class="fa-duotone fa-circle-check summary-icon"></i>
                <span class="summary-label">Done</span>
              </div>
              <div class="summary-value">{{ summary.done }}</div>
            </div>
          </div>
        </div>

        <div class="summary-section">
          <div class="section-header">
            <div class="section-title-wrap">
              <i class="fa-duotone fa-tags section-icon"></i>
              <span class="section-title">By Request Type</span>
            </div>
          </div>
          <div v-if="requestTypeCards.length" class="section-cards type-cards">
            <div class="summary-card" v-for="card in requestTypeCards" :key="card.value">
              <div class="summary-card-header">
                <i :class="['fa-duotone', card.icon, 'summary-icon']"></i>
                <span class="summary-label" :title="card.label">{{ card.label }}</span>
              </div>
              <div class="summary-value">{{ card.count }}</div>
            </div>
          </div>
          <!-- Cards follow the filtered rows, so this shows on an empty queue or a filter with no hits. -->
          <div v-else class="type-cards-empty">No requests in this view.</div>
        </div>
      </div>

      <div class="toolbar single-line-toolbar">
        <input v-model.trim="search"
               type="text"
               class="search-input"
               placeholder="Search loan, borrower, org, LO, request type..." />

        <select v-model="statusFilter" class="filter-select toolbar-select">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>

        <select v-model="requestTypeFilter" class="filter-select toolbar-select">
          <option value="">All Request Types</option>
          <option v-for="opt in requestTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <select v-model="assignedFilter" class="filter-select toolbar-select">
          <option value="">All Assignees</option>
          <option value="mine">Assigned to Me</option>
          <option value="unassigned">Unassigned</option>
          <option v-if="uniqueAssignees.length" disabled>──────────</option>
          <option v-for="u in uniqueAssignees" :key="u.id" :value="u.id">{{ u.name }}</option>
        </select>

        <select v-model="channelFilter" class="filter-select toolbar-select">
          <option value="">All Channels</option>
          <option v-for="item in channelOptions" :key="item" :value="item">
            {{ displayChannel(item) }}
          </option>
        </select>
      </div>

      <div class="table-card">
        <div class="table-card-header">
          <div class="table-card-title-wrap">
            <i class="fa-duotone fa-inbox table-card-icon"></i>
            <span class="table-card-title">Lock Desk Requests</span>
          </div>
          <div class="table-card-count">{{ filteredRows.length }}</div>
        </div>

        <div v-if="loading" class="table-state">Loading lock desk queue...</div>
        <div v-else-if="errorMessage" class="table-state error">{{ errorMessage }}</div>
        <div v-else-if="pagedRows.length === 0" class="table-state">No items found.</div>

        <div v-else class="table-wrap">
          <table class="locks-table">
            <thead>
              <tr>
                <th @click="setSort('arrived_date')">
                  Arrived
                  <span>{{ sortIcon('arrived_date') }}</span>
                </th>
                <th @click="setSort('loan_number')">
                  Loan
                  <span>{{ sortIcon('loan_number') }}</span>
                </th>
                <th @click="setSort('borrower_last_name')">
                  Borrower
                  <span>{{ sortIcon('borrower_last_name') }}</span>
                </th>
                <th @click="setSort('request_type')">
                  Request Type
                  <span>{{ sortIcon('request_type') }}</span>
                </th>
                <th @click="setSort('status')">
                  Status
                  <span>{{ sortIcon('status') }}</span>
                </th>
                <th @click="setSort('assigned_to_name')">
                  Assigned To
                  <span>{{ sortIcon('assigned_to_name') }}</span>
                </th>
                <th @click="setSort('orgid')">
                  Org
                  <span>{{ sortIcon('orgid') }}</span>
                </th>
                <th @click="setSort('loan_officer_name')">
                  LO
                  <span>{{ sortIcon('loan_officer_name') }}</span>
                </th>
                <th @click="setSort('channel')">
                  Channel
                  <span>{{ sortIcon('channel') }}</span>
                </th>
                <th @click="setSort('has_notes')">
                  Notes
                  <span>{{ sortIcon('has_notes') }}</span>
                </th>
                <th>Links</th>
                <th>Started By</th>
                <th>Started At</th>
                <th>Completed By</th>
                <th>Completed At</th>
                <th title="Turnaround — time from arrival in the queue to completion">Duration</th>
                <th title="Wait — time from arrival to first pickup (assignment)">Arrival → Pickup</th>
                <th title="Pickup — time from assignment to completion">Pickup → Done</th>
                <th>Credited To</th>
                <th title="Number of times this item changed hands">Reassigned</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="row in pagedRows"
                  :key="row.id || row.loan_id"
                  :class="{ 'done-row': row.status === 'done' }">
                <td>{{ formatDateTime(row.arrived_date || row.created_date || row.created_utc, true) }}</td>

                <td>
                  <div class="primary-text">{{ row.loan_number || '—' }}</div>
                </td>

                <td>
                  <div class="primary-text">{{ borrowerName(row) }}</div>
                </td>

                <td>
                  <div class="type-cell">
                    <div class="type-pill-row">
                      <span class="type-pill" :class="requestTypeClass(row.request_type)">
                        {{ requestTypeLabel(row.request_type) }}
                      </span>
                      <span v-if="row.status !== 'done' && row.open_duplicate_count > 0"
                            class="dup-flag"
                            :title="`${row.open_duplicate_count + 1} open ${requestTypeLabel(row.request_type)} requests on this loan — possible duplicate`">
                        <i class="fa-duotone fa-clone"></i> +{{ row.open_duplicate_count }}
                      </span>
                    </div>
                    <span v-if="row.request_description" class="request-desc" :title="row.request_description">
                      {{ row.request_description }}
                    </span>
                  </div>
                </td>

                <td>
                  <button class="status-pill-btn"
                          :class="statusClass(row.status)"
                          :disabled="row.status_loading"
                          @click="openStatusModal(row)">
                    {{ statusLabel(row.status) }}
                  </button>
                </td>

                <td>
                  <div class="assigned-cell">
                    <span class="assigned-name">
                      {{ row.assigned_to_name || '—' }}
                    </span>
                    <button class="claim-btn"
                            :class="{ 'is-mine': row.assigned_to_azure_id && row.assigned_to_azure_id.toLowerCase() === currentAzureId }"
                            :disabled="row.status_loading"
                            @click="claimItem(row)">
                      {{ row.assigned_to_azure_id && row.assigned_to_azure_id.toLowerCase() === currentAzureId
                          ? 'Mine'
                          : (row.assigned_to_azure_id ? 'Reassign' : 'Claim') }}
                    </button>
                    <button v-if="isLockDeskManager"
                            class="assign-to-btn"
                            :disabled="row.status_loading"
                            :title="row.assigned_to_azure_id ? 'Assign to a different user' : 'Assign to a specific user'"
                            @click="openAssignModal(row)">
                      <i class="fa-duotone fa-user-pen"></i>
                    </button>
                  </div>
                </td>

                <td>{{ row.orgid || '—' }}</td>

                <td>{{ row.loan_officer_name || row.loan_officer || '—' }}</td>

                <td class="compact-cell">{{ displayChannel(row.channel) }}</td>

                <td>
                  <button class="notes-btn"
                          :class="row.has_notes ? 'has-notes' : 'no-notes'"
                          :disabled="!row.id && !row.loan_id"
                          @click="openNotesModal(row)">
                    <i :class="row.has_notes ? 'fa-duotone fa-messages' : 'fa-duotone fa-message-plus'"></i>
                    <span>{{ row.has_notes ? 'View' : 'Add' }}</span>
                  </button>
                </td>

                <td>
                  <div class="link-actions">
                    <button
                      class="polly-btn"
                      :disabled="!row.loan_id"
                      :title="row.loan_id ? 'Open this loan in Polly' : 'No loan id — cannot open Polly'"
                      @click="openPolly(row)">
                      Open Polly
                    </button>
                    <button
                      class="encompass-btn"
                      :disabled="!row.loan_id"
                      :title="row.loan_id ? 'Open this loan in Encompass on the web' : 'No loan id — cannot open Encompass'"
                      @click="openEncompass(row)">
                      Open Encompass
                    </button>
                  </div>
                </td>
                <td class="tracking-cell">{{ row.started_by_name || '—' }}</td>
                <td class="tracking-cell">{{ formatTrackingDate(row.started_utc) }}</td>
                <td class="tracking-cell">{{ row.completed_by_name || '—' }}</td>
                <td class="tracking-cell">{{ formatTrackingDate(row.completed_utc) }}</td>
                <td class="tracking-cell">{{ formatDuration(row.duration_seconds) }}</td>
                <td class="tracking-cell">{{ formatDuration(row.wait_seconds) }}</td>
                <td class="tracking-cell">{{ formatDuration(row.pickup_seconds) }}</td>
                <td class="tracking-cell">
                  <span v-if="row.credited_by_name" class="credited-pill"
                        :class="{ 'credited-diff': row.started_by_name && row.completed_by_name && row.started_by_name !== row.completed_by_name }">
                    {{ row.credited_by_name }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td class="tracking-cell">
                  <button v-if="row.reassignment_count > 0"
                          class="reassign-count-btn"
                          @click="openAssignmentHistory(row)"
                          title="View assignment history">
                    {{ row.reassignment_count }}×
                  </button>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="filteredRows.length" class="pagination-bar">
        <div class="pagination-info">
          Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }}
        </div>
        <div class="pagination-actions">
          <button class="page-btn" @click="prevPage" :disabled="page === 1">Prev</button>
          <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" @click="nextPage" :disabled="page === totalPages">Next</button>
        </div>
      </div>

      <!-- Insights Modal -->
      <div v-if="showInsightsModal" class="modal-backdrop" @click.self="closeInsights">
        <div class="modal-card insights-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Queue Insights</h3>
              <p class="modal-subtitle">{{ timeFrameLabel }} — {{ insightRows.length }} item{{ insightRows.length === 1 ? '' : 's' }}</p>
            </div>
            <button class="close-btn" @click="closeInsights">✕</button>
          </div>

          <div class="modal-body insights-modal-body">
            <div class="insights-split">

              <!-- Left: filters + charts + brief -->
              <div class="insights-left">
                <div class="insights-filter-bar">
                  <select v-model="insightFilters.request_type" class="filter-select insights-filter-select">
                    <option value="">All Types</option>
                    <option v-for="opt in requestTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  </select>
                  <select v-model="insightFilters.status" class="filter-select insights-filter-select">
                    <option value="">All Statuses</option>
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="done">Done</option>
                  </select>
                  <select v-model="insightFilters.assignee" class="filter-select insights-filter-select">
                    <option value="">All Assignees</option>
                    <option value="unassigned">Unassigned</option>
                    <option v-for="u in uniqueAssignees" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                  <select v-model="insightFilters.channel" class="filter-select insights-filter-select">
                    <option value="">All Channels</option>
                    <option v-for="item in channelOptions" :key="item" :value="item">{{ displayChannel(item) }}</option>
                  </select>
                  <select v-model="insightFilters.orgid" class="filter-select insights-filter-select">
                    <option value="">All Orgs</option>
                    <option v-for="o in insightOrgOptions" :key="o" :value="o">{{ o }}</option>
                  </select>
                  <button v-if="insightFiltersActive" class="insights-clear-btn" @click="resetInsightFilters" title="Clear filters">
                    <i class="fa-duotone fa-filter-circle-xmark"></i> Clear
                  </button>
                </div>

                <div v-if="!insightRows.length" class="insights-empty">No items match the selected filters.</div>

                <template v-else>
                  <div class="insights-charts">
                    <div class="insight-chart-card">
                      <div class="insight-chart-title">By Status</div>
                      <Chart>
                        <ChartArea :height="145" background="transparent" />
                        <ChartLegend :position="'bottom'" />
                        <ChartTooltip :visible="true" :format="'{0}'" />
                        <ChartSeries>
                          <ChartSeriesItem :type="'donut'" :data-items="statusChartData"
                                           :category-field="'label'" :field="'value'" :color-field="'color'"
                                           :labels="donutLabels" />
                        </ChartSeries>
                      </Chart>
                    </div>

                    <div class="insight-chart-card">
                      <div class="insight-chart-title">By Request Type</div>
                      <Chart>
                        <ChartArea :height="145" background="transparent" />
                        <ChartLegend :position="'bottom'" />
                        <ChartTooltip :visible="true" :format="'{0}'" />
                        <ChartSeries>
                          <ChartSeriesItem :type="'donut'" :data-items="typeChartData"
                                           :category-field="'label'" :field="'value'" :color-field="'color'"
                                           :labels="donutLabels" />
                        </ChartSeries>
                      </Chart>
                    </div>

                    <div class="insight-chart-card insight-chart-card-wide">
                      <div class="insight-chart-title">Who's Doing What</div>
                      <div v-if="assigneeChart.hasData" class="assignee-bars">
                        <div v-for="(name, i) in assigneeChart.categories" :key="i" class="assignee-row">
                          <span class="assignee-row-name" :title="name">{{ name }}</span>
                          <div class="assignee-row-track">
                            <div class="assignee-bar-done"
                                 :style="{ width: (assigneeChart.done[i] / assigneeBarMax * 100) + '%' }"
                                 :title="assigneeChart.done[i] + ' done'" />
                            <div class="assignee-bar-open"
                                 :style="{ width: (assigneeChart.open[i] / assigneeBarMax * 100) + '%' }"
                                 :title="assigneeChart.open[i] + ' open'" />
                          </div>
                          <span class="assignee-row-count">{{ assigneeChart.open[i] + assigneeChart.done[i] }}</span>
                        </div>
                        <div class="assignee-legend">
                          <span><span class="assignee-swatch assignee-swatch-done"></span>Done</span>
                          <span><span class="assignee-swatch assignee-swatch-open"></span>Open</span>
                        </div>
                      </div>
                    </div>

                    <div class="insight-chart-card insight-chart-card-wide">
                      <div class="insight-chart-title">Timing</div>
                      <div class="timing-stages">
                        <div class="timing-stage">
                          <span class="timing-stage-value">{{ formatDuration(stageAverages.wait) }}</span>
                          <span class="timing-stage-label">Avg wait to pickup</span>
                        </div>
                        <div class="timing-stage">
                          <span class="timing-stage-value">{{ formatDuration(stageAverages.pickup) }}</span>
                          <span class="timing-stage-label">Avg pickup → done</span>
                        </div>
                        <div class="timing-stage">
                          <span class="timing-stage-value">{{ formatDuration(stageAverages.turnaround) }}</span>
                          <span class="timing-stage-label">Avg turnaround</span>
                        </div>
                      </div>
                      <div v-if="pickupByAssignee.hasData" class="timing-assignee">
                        <div class="insight-chart-subtitle">Avg handling time by assignee (slowest first)</div>
                        <div v-for="(a, i) in pickupByAssignee.rows" :key="i" class="timing-row">
                          <span class="timing-row-name" :title="a.name">{{ a.name }}</span>
                          <div class="timing-track">
                            <div class="timing-bar"
                                 :style="{ width: (a.avg / pickupByAssignee.max * 100) + '%' }"
                                 :title="formatDuration(a.avg) + ' avg over ' + a.count + ' item' + (a.count === 1 ? '' : 's')" />
                          </div>
                          <span class="timing-row-value">{{ formatDuration(a.avg) }}</span>
                        </div>
                      </div>
                      <div v-else class="timing-empty">No completed items with handling time yet.</div>
                    </div>

                    <!-- Extension-request level, so it sits on its own data (not insightRows) and
                         deliberately ignores the modal filters — those are queue-item concepts. -->
                    <div class="insight-chart-card insight-chart-card-wide">
                      <div class="insight-chart-title">Lock Extension Requests</div>

                      <div v-if="extStatsLoading" class="timing-empty">Loading extension stats…</div>
                      <div v-else-if="extStatsError" class="timing-empty">{{ extStatsError }}</div>
                      <div v-else-if="!extStats || !extStats.total_requests" class="timing-empty">
                        No lock extension requests in this time frame.
                      </div>
                      <template v-else>
                        <div class="insight-chart-subtitle ext-scope">
                          {{ extStats.total_requests }} request{{ extStats.total_requests === 1 ? '' : 's' }}
                          <span v-if="extRangeLabel"> · {{ extRangeLabel }}</span>
                          · not affected by the filters above
                        </div>

                        <div class="ext-split">
                          <div class="ext-block">
                            <div class="ext-block-title">Market based vs at cost</div>
                            <div class="ext-block-sub">
                              {{ extTypeSplit.classified }} of {{ extStats.total_requests }} requests carry a type flag
                            </div>

                            <template v-if="extTypeSplit.classified">
                              <div class="ext-bar">
                                <div v-for="b in extTypeSplit.bars" :key="b.key"
                                     class="ext-bar-seg"
                                     :style="{ flexGrow: b.pct, background: b.color }"
                                     :title="b.label + ' — ' + b.value + ' (' + b.pct.toFixed(1) + '%)'" />
                              </div>
                              <div class="ext-legend">
                                <div v-for="b in extTypeSplit.bars" :key="b.key" class="ext-legend-item">
                                  <span class="ext-legend-head">
                                    <span class="ext-swatch" :style="{ background: b.color }" />{{ b.label }}
                                  </span>
                                  <span class="ext-legend-val">
                                    {{ b.pct.toFixed(1) }}%
                                    <span class="ext-legend-count">{{ b.value }} request{{ b.value === 1 ? '' : 's' }}</span>
                                  </span>
                                </div>
                              </div>
                            </template>
                            <div v-else class="timing-empty">No requests in this window carry a type flag.</div>

                            <div v-if="extTypeSplit.unknown" class="ext-note">
                              <span class="ext-swatch ext-swatch-muted" />
                              {{ extTypeSplit.unknown }} request{{ extTypeSplit.unknown === 1 ? '' : 's' }}
                              ({{ extTypeSplit.unknownPct.toFixed(1) }}% of all {{ extStats.total_requests }})
                              have no type flag — excluded above
                            </div>
                          </div>

                          <div class="ext-block">
                            <div class="ext-block-title">Auto-approved vs manual required</div>
                            <div class="ext-block-sub">All {{ extStats.total_requests }} requests</div>

                            <div class="ext-bar">
                              <div v-for="b in extDecisionSplit.bars" :key="b.key"
                                   class="ext-bar-seg"
                                   :style="{ flexGrow: b.pct, background: b.color }"
                                   :title="b.label + ' — ' + b.value + ' (' + b.pct.toFixed(1) + '%)'" />
                            </div>
                            <div class="ext-legend">
                              <div v-for="b in extDecisionSplit.bars" :key="b.key" class="ext-legend-item">
                                <span class="ext-legend-head">
                                  <span class="ext-swatch" :style="{ background: b.color }" />{{ b.label }}
                                </span>
                                <span class="ext-legend-val">
                                  {{ b.pct.toFixed(1) }}%
                                  <span class="ext-legend-count">{{ b.value }} request{{ b.value === 1 ? '' : 's' }}</span>
                                </span>
                                <span v-if="b.detail" class="ext-legend-detail">{{ b.detail }}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div v-if="extTypeSplit.unknown" class="ext-why">
                          <span class="ext-why-label">Why {{ extTypeSplit.unknown }} are unclassifiable:</span>
                          the type flag is only written during the pricing step. Requests that exit early as
                          <code>exception_needed</code> never reach it, so no flag exists — {{ extTypeSplit.unknownException }}
                          of the {{ extTypeSplit.unknown }}.
                        </div>
                      </template>
                    </div>
                  </div>

                  <ul class="insights-list">
                    <li v-for="(line, i) in queueInsights.lines" :key="i" class="insight-line">
                      <i class="fa-duotone" :class="line.icon"></i>
                      <span>{{ line.text }}</span>
                    </li>
                  </ul>
                </template>
              </div>

              <!-- Right: AI panel -->
              <div class="insights-ai-panel">
                <div class="insights-ai-label">
                  <i class="fa-duotone fa-sparkles"></i> Ask swiftAI
                </div>
                <div class="insights-ai-body">
                  <div v-if="!aiMessages.length && !aiLoading && !aiError" class="insights-ai-placeholder">
                    Ask anything about the current queue data — summarize, compare assignees, or identify trends.
                  </div>
                  <template v-for="(msg, i) in aiMessages" :key="i">
                    <div v-if="msg.role === 'user'" class="ai-bubble ai-bubble-user">{{ msg.text }}</div>
                    <div v-else class="ai-bubble ai-bubble-assistant">
                      <div class="insights-ai-formatted" v-html="formatAiResponse(msg.text)"></div>
                    </div>
                  </template>
                  <div v-if="aiLoading" class="ai-bubble ai-bubble-assistant ai-bubble-typing">
                    <span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span>
                  </div>
                  <div v-if="aiError" class="insights-ai-error">{{ aiError }}</div>
                </div>
                <div class="insights-ai-input-row">
                  <input
                    v-model="aiPrompt"
                    class="insights-ai-input"
                    placeholder="Ask a question…"
                    @keydown.enter.prevent="sendAiPrompt"
                    :disabled="aiLoading"
                  />
                  <button class="insights-ai-btn" @click="sendAiPrompt" :disabled="aiLoading || !aiPrompt.trim()">
                    Ask
                  </button>
                </div>
              </div>

            </div>

            <div class="modal-footer-actions">
              <button class="save-btn" @click="copyInsights">
                {{ insightsCopied ? 'Copied!' : 'Copy Summary' }}
              </button>
              <button class="cancel-btn" @click="closeInsights">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Status Modal -->
      <div v-if="showStatusModal" class="modal-backdrop" @click.self="closeStatusModal">
        <div class="modal-card status-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Update Status</h3>
              <div class="modal-subtitle">
                {{ borrowerName(selectedStatusRow) }}
                <span v-if="selectedStatusRow.loan_number"> — {{ selectedStatusRow.loan_number }}</span>
              </div>
            </div>
            <button class="close-btn" @click="closeStatusModal">✕</button>
          </div>

          <div class="modal-body">
            <div class="status-options">
              <button class="status-option-btn"
                      :class="{ active: pendingStatus === 'not_started' }"
                      @click="pendingStatus = 'not_started'">
                <i class="fa-duotone fa-circle-dashed"></i>
                Not Started
              </button>
              <button class="status-option-btn in-progress-option"
                      :class="{ active: pendingStatus === 'in_progress' }"
                      @click="pendingStatus = 'in_progress'">
                <i class="fa-duotone fa-spinner"></i>
                In Progress
              </button>
              <button class="status-option-btn done-option"
                      :class="{ active: pendingStatus === 'done' }"
                      @click="pendingStatus = 'done'">
                <i class="fa-duotone fa-circle-check"></i>
                Done
              </button>
            </div>

            <div v-if="statusErrorMessage" class="inline-error">{{ statusErrorMessage }}</div>

            <div class="modal-footer-actions">
              <button class="save-btn"
                      :disabled="statusSaving || pendingStatus === (selectedStatusRow.status || 'not_started')"
                      @click="submitStatus">
                {{ statusSaving ? 'Saving...' : 'Update Status' }}
              </button>
              <button class="cancel-btn" @click="closeStatusModal">Cancel</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Modal -->
      <div v-if="showCreateModal" class="modal-backdrop" @click.self="closeCreateModal">
        <div class="modal-card create-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">New Request</h3>
              <p class="modal-subtitle">Add a new item to the Lock Desk Queue</p>
            </div>
            <button class="close-btn" @click="closeCreateModal">✕</button>
          </div>

          <div class="modal-body">
            <div class="create-mode-tabs">
              <button class="create-mode-tab" :class="{ active: createMode === 'manual' }" @click="createMode = 'manual'">
                <i class="fa-duotone fa-pen-to-square"></i> Manual
              </button>
              <button class="create-mode-tab" :class="{ active: createMode === 'ai' }" @click="createMode = 'ai'">
                <i class="fa-duotone fa-sparkles"></i> AI Assist
              </button>
            </div>

            <div v-if="createMode === 'ai'" class="ai-assist-section" @paste="onAiPaste">
              <div class="ai-assist-input-group">
                <label class="form-label">Paste email, notes, or description</label>
                <textarea v-model.trim="aiText"
                          class="form-textarea ai-text-input"
                          rows="5"
                          placeholder="Paste an email, request description, or any relevant text here..."></textarea>
              </div>
              <div class="ai-assist-input-group">
                <label class="form-label">Screenshot <span class="ai-optional-hint">(optional)</span></label>
                <div class="ai-file-drop" :class="{ 'has-file': aiImageFile }" @click="$refs.aiFileInput.click()">
                  <i v-if="!aiImageFile" class="fa-duotone fa-image-landscape ai-file-icon"></i>
                  <i v-else class="fa-duotone fa-circle-check ai-file-icon ai-file-ok"></i>
                  <span>{{ aiImageFile ? aiImageFile.name : 'Paste (Ctrl+V) or click to select an image...' }}</span>
                </div>
                <input ref="aiFileInput" type="file" class="ai-file-hidden" accept="image/*" @change="onAiImageSelect" />
              </div>
              <div v-if="aiError" class="error-banner">{{ aiError }}</div>
              <div class="ai-assist-actions">
                <button class="ai-generate-btn" :disabled="aiGenerating || (!aiText && !aiImageFile)" @click="generateFromAI">
                  <i class="fa-duotone fa-sparkles"></i>
                  {{ aiGenerating ? 'Generating...' : 'Generate' }}
                </button>
                <span v-if="aiGenerating" class="ai-generating-hint">Analyzing content with AI...</span>
              </div>
            </div>

            <div v-if="createMode === 'manual'">
              <div v-if="aiPopulated" class="ai-populated-banner">
                <i class="fa-duotone fa-sparkles"></i> AI populated this form — review and edit before saving.
              </div>

              <div class="create-form-grid">
                <div class="form-group form-group-full">
                  <label class="form-label">Request Type <span class="required-star">*</span></label>
                  <select v-model="createForm.request_type" class="form-select">
                    <option value="" disabled>Select type...</option>
                    <option value="lock_extension">Lock Extension</option>
                    <option value="reprice">Reprice</option>
                    <option value="float_down">Float Down</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div class="form-group">
                  <label class="form-label">Loan Number</label>
                  <input v-model.trim="createForm.loan_number" class="form-input" type="text" placeholder="e.g. 1234567" />
                </div>

                <div class="form-group lo-picker-group">
                  <label class="form-label">Loan Officer</label>
                  <input v-model.trim="createForm.loan_officer_name"
                         class="form-input"
                         type="text"
                         placeholder="Search a name or type…"
                         autocomplete="off"
                         @focus="showLoDropdown = true"
                         @input="showLoDropdown = true"
                         @blur="hideLoDropdownSoon" />
                  <div v-if="showLoDropdown && loSearchResults.length" class="user-dropdown">
                    <button v-for="u in loSearchResults"
                            :key="u.key"
                            type="button"
                            class="user-dropdown-row"
                            @mousedown.prevent="selectLoUser(u)">
                      <span class="user-dropdown-name">{{ u.name }}</span>
                      <span v-if="u.email" class="user-dropdown-email">{{ u.email }}</span>
                    </button>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">LO Email</label>
                  <input v-model.trim="createForm.loan_officer_email"
                         class="form-input"
                         type="email"
                         placeholder="loanofficer@example.com" />
                </div>

                <div class="form-group">
                  <label class="form-label">Borrower First</label>
                  <input v-model.trim="createForm.borrower_first" class="form-input" type="text" placeholder="First name" />
                </div>

                <div class="form-group">
                  <label class="form-label">Borrower Last</label>
                  <input v-model.trim="createForm.borrower_last" class="form-input" type="text" placeholder="Last name" />
                </div>

                <div class="form-group">
                  <label class="form-label">Org</label>
                  <input v-model.trim="createForm.orgid" class="form-input" type="text" placeholder="Org ID" />
                </div>

                <div class="form-group">
                  <label class="form-label">Channel</label>
                  <input v-model.trim="createForm.channel" class="form-input" type="text" placeholder="Channel" />
                </div>

                <div class="form-group form-group-full">
                  <label class="form-label">Description</label>
                  <textarea v-model.trim="createForm.request_description"
                            class="form-textarea"
                            rows="3"
                            placeholder="Optional details about this request..."></textarea>
                </div>
              </div>

              <div v-if="aiGeneratedNote" class="ai-note-group">
                <label class="form-label">
                  <i class="fa-duotone fa-sparkles"></i> AI Note
                  <span class="ai-note-hint">Will be saved as a note on this request</span>
                </label>
                <textarea v-model.trim="aiGeneratedNote" class="form-textarea" rows="3"></textarea>
              </div>

              <div v-if="createErrorMessage" class="error-banner">{{ createErrorMessage }}</div>

              <div class="modal-footer-actions">
                <button class="cancel-btn" @click="closeCreateModal" :disabled="createSaving">Cancel</button>
                <button class="save-btn"
                        @click="submitCreateItem"
                        :disabled="createSaving || !createForm.request_type">
                  {{ createSaving ? 'Creating...' : 'Create Request' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showNotesModal" class="modal-backdrop" @click.self="closeNotesModal">
        <div class="modal-card notes-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Notes</h3>
              <div class="modal-subtitle">
                {{ borrowerName(selectedNotesRow) }}
                <span v-if="selectedNotesRow.loan_number"> — {{ selectedNotesRow.loan_number }}</span>
              </div>
            </div>
            <button class="close-btn" @click="closeNotesModal">✕</button>
          </div>

          <div class="modal-body notes-modal-body">
            <div v-if="notesLoading" class="table-state">Loading notes...</div>
            <div v-else-if="notesErrorMessage" class="table-state error">{{ notesErrorMessage }}</div>
            <div v-else-if="!notes.length" class="table-state">No notes yet. Add the first one below.</div>

            <div v-else class="notes-list">
              <div v-for="(note, idx) in notes"
                   :key="note.id || idx"
                   class="note-item"
                   :class="{ 'own-note': isOwnNote(note) }">
                <div class="note-header">
                  <span class="note-author">{{ getNoteAuthor(note) }}</span>
                  <span class="note-scope-badge"
                        :class="getNoteIsExternal(note) ? 'scope-external' : 'scope-internal'">
                    {{ getNoteIsExternal(note) ? 'External' : 'Internal' }}
                  </span>
                  <span class="note-date">{{ formatNoteDateTime(getNoteCreatedDate(note)) }}</span>
                  <button v-if="canDeleteNote(note) && pendingDeleteNoteId !== note.id"
                          class="note-delete-btn"
                          title="Delete note"
                          @click="promptDeleteNote(note)">
                    <i class="fa-duotone fa-trash-can"></i>
                  </button>
                </div>
                <div class="note-body">{{ getNoteText(note) }}</div>
                <div v-if="pendingDeleteNoteId === note.id" class="note-delete-confirm">
                  <span class="note-delete-text">Delete this note?</span>
                  <button class="note-delete-yes"
                          :disabled="noteDeletingId === note.id"
                          @click="confirmDeleteNote(note)">
                    {{ noteDeletingId === note.id ? 'Deleting…' : 'Delete' }}
                  </button>
                  <button class="note-delete-no"
                          :disabled="noteDeletingId === note.id"
                          @click="cancelDeleteNote">
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <div class="notes-composer" :class="composeExternal ? 'composer-external' : 'composer-internal'">
              <div class="compose-scope-row">
                <span class="compose-scope-label">Note type</span>
                <div class="scope-toggle" role="group" aria-label="Note type">
                  <button type="button"
                          class="scope-toggle-btn"
                          :class="{ active: !composeExternal }"
                          :disabled="notesSaving"
                          @click="composeExternal = false">
                    Internal
                  </button>
                  <button type="button"
                          class="scope-toggle-btn"
                          :class="{ active: composeExternal, 'active-external': composeExternal }"
                          :disabled="notesSaving"
                          @click="composeExternal = true">
                    External
                  </button>
                </div>
                <span class="compose-scope-hint">
                  {{ composeExternal ? '🌐 Shared with the loan officer' : '🔒 Lock desk only' }}
                </span>
              </div>

              <textarea v-model.trim="newNoteText"
                        class="notes-textarea"
                        rows="3"
                        :placeholder="composeExternal
                          ? 'Type an external note (shared with the loan officer)…'
                          : 'Type an internal note (lock desk only)…'"
                        :disabled="notesSaving || noteSending"
                        @keydown.enter.exact.prevent="submitNote"></textarea>

              <div class="note-recipients">
                <label class="recipients-label">Send to (optional)</label>
                <div class="recipients-box">
                  <span v-for="(r, idx) in noteRecipients" :key="(r.email || '') + idx" class="recipient-chip">
                    <span class="recipient-chip-text" :title="r.email">{{ r.name || r.email }}</span>
                    <button type="button" class="recipient-chip-x" :disabled="noteSending" @click="removeRecipient(idx)">✕</button>
                  </span>
                  <input v-model.trim="recipientSearch"
                         class="recipients-input"
                         type="text"
                         placeholder="Search a name or type an email, Enter to add…"
                         autocomplete="off"
                         :disabled="noteSending"
                         @focus="showRecipientDropdown = true"
                         @input="showRecipientDropdown = true"
                         @keydown.enter.prevent="addRecipientEmail"
                         @blur="hideRecipientDropdownSoon" />
                  <div v-if="showRecipientDropdown && recipientMatches.length" class="user-dropdown recipients-dropdown">
                    <button v-for="u in recipientMatches"
                            :key="u.key"
                            type="button"
                            class="user-dropdown-row"
                            @mousedown.prevent="addRecipientUser(u)">
                      <span class="user-dropdown-name">{{ u.name }}</span>
                      <span v-if="u.email" class="user-dropdown-email">{{ u.email }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="notes-composer-actions">
                <div class="notes-compose-hint">
                  {{ noteRecipients.length
                      ? ('Send will email ' + noteRecipients.length + ' recipient' + (noteRecipients.length === 1 ? '' : 's'))
                      : 'Press Enter to save' }}
                </div>
                <div class="composer-buttons">
                  <button class="save-btn ghost-save-btn"
                          :disabled="notesSaving || noteSending || !newNoteText"
                          @click="submitNote">
                    {{ notesSaving ? 'Saving...' : 'Save Note' }}
                  </button>
                  <button class="save-btn send-note-btn"
                          :disabled="noteSending || notesSaving || !newNoteText || !noteRecipients.length"
                          @click="sendNote"
                          title="Save this note and email it to the recipients">
                    {{ noteSending ? 'Sending...' : 'Send' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assignment History Modal -->
      <div v-if="showAssignmentModal" class="modal-backdrop" @click.self="closeAssignmentHistory">
        <div class="modal-card notes-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Assignment History</h3>
              <div class="modal-subtitle">
                {{ borrowerName(selectedAssignmentRow) }}
                <span v-if="selectedAssignmentRow.loan_number"> — {{ selectedAssignmentRow.loan_number }}</span>
              </div>
            </div>
            <button class="close-btn" @click="closeAssignmentHistory">✕</button>
          </div>

          <div class="modal-body notes-modal-body">
            <div v-if="assignmentLoading" class="table-state">Loading history...</div>
            <div v-else-if="assignmentError" class="table-state error">{{ assignmentError }}</div>
            <div v-else-if="!assignmentHistory.length" class="table-state">No assignment history recorded.</div>

            <ol v-else class="assignment-timeline">
              <li v-for="(a, idx) in assignmentHistory" :key="a.id || idx" class="assignment-event">
                <span class="assignment-badge" :class="a.is_reassignment ? 'badge-reassign' : 'badge-pickup'">
                  {{ a.is_reassignment ? 'Reassigned' : 'Picked up' }}
                </span>
                <div class="assignment-detail">
                  <div class="assignment-to">{{ a.assigned_to_name || '—' }}</div>
                  <div class="assignment-meta">
                    {{ formatNoteDateTime(a.created_utc) }}
                    <span v-if="a.assigned_by_name && a.assigned_by_name !== a.assigned_to_name">
                      · by {{ a.assigned_by_name }}
                    </span>
                  </div>
                </div>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <!-- Assign To Modal (managers only) -->
      <div v-if="showAssignModal" class="modal-backdrop" @click.self="closeAssignModal">
        <div class="modal-card assign-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Assign Request</h3>
              <div class="modal-subtitle">
                {{ borrowerName(selectedAssignRow) }}
                <span v-if="selectedAssignRow.loan_number"> — {{ selectedAssignRow.loan_number }}</span>
              </div>
            </div>
            <button class="close-btn" @click="closeAssignModal">✕</button>
          </div>

          <div class="modal-body assign-modal-body">
            <div v-if="selectedAssignRow.assigned_to_name" class="assign-current">
              Currently assigned to <strong>{{ selectedAssignRow.assigned_to_name }}</strong>
            </div>

            <div class="assign-search-wrap">
              <i class="fa-duotone fa-magnifying-glass assign-search-icon"></i>
              <input v-model.trim="assignSearch"
                     ref="assignSearchInput"
                     type="text"
                     name="lockDeskAssignSearch"
                     class="assign-search-input"
                     autocomplete="off"
                     autocorrect="off"
                     autocapitalize="off"
                     spellcheck="false"
                     placeholder="Search a name or email..." />
            </div>

            <div v-if="assignError" class="inline-error">{{ assignError }}</div>

            <div class="assign-list">
              <div v-if="assignUsersLoading" class="assign-list-state">Loading users...</div>

              <template v-else>
                <button v-for="u in filteredAssignUsers"
                        :key="u.key"
                        class="assign-user-row"
                        :class="{ 'is-current': isRowAssignee(u), 'from-directory': u.source === 'directory' }"
                        :disabled="assignSaving"
                        @click="assignToUser(u)">
                  <span class="assign-user-avatar">{{ userInitials(u.name) }}</span>
                  <span class="assign-user-info">
                    <span class="assign-user-name">{{ u.name }}</span>
                    <span v-if="u.email" class="assign-user-email">{{ u.email }}</span>
                  </span>
                  <span v-if="u.source === 'directory'" class="assign-user-tag" title="From the org directory — will be added to the roster">Directory</span>
                  <span v-else-if="isRowAssignee(u)" class="assign-user-tag current-tag">Current</span>
                </button>

                <div v-if="!filteredAssignUsers.length && !showAddNewForm" class="assign-list-state">
                  No matching users.
                </div>
              </template>
            </div>

            <!-- Add new roster user -->
            <div class="assign-add-section">
              <button v-if="!showAddNewForm"
                      class="assign-add-toggle"
                      @click="openAddNewForm">
                <i class="fa-duotone fa-user-plus"></i>
                Add {{ assignSearch ? `"${assignSearch}"` : 'a new user' }}
              </button>

              <div v-else class="assign-add-form">
                <div class="assign-add-title">New assignable user</div>
                <div class="assign-add-grid">
                  <input v-model.trim="newUser.name" class="form-input" type="text" placeholder="Full name *" />
                  <input v-model.trim="newUser.email" class="form-input" type="text" placeholder="Email (optional)" />
                </div>
                <div class="assign-add-hint">
                  Saved to the shared roster so any manager can assign to them.
                </div>
                <div class="assign-add-actions">
                  <button class="cancel-btn" :disabled="assignSaving" @click="cancelAddNewForm">Cancel</button>
                  <button class="save-btn"
                          :disabled="assignSaving || !newUser.name"
                          @click="saveAndAssignNewUser">
                    {{ assignSaving ? 'Saving...' : 'Save & Assign' }}
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-footer-actions">
              <button v-if="selectedAssignRow.assigned_to_azure_id"
                      class="assign-unassign-btn"
                      :disabled="assignSaving"
                      @click="unassignRow">
                Unassign
              </button>
              <button class="cancel-btn" @click="closeAssignModal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Concession Approvers importer (managers only) -->
      <div v-if="showApproverModal" class="modal-backdrop" @click.self="closeApproverImport">
        <div class="modal-card approver-modal-card">
          <div class="modal-header">
            <div>
              <h3 class="modal-title">Concession Approvers</h3>
              <div class="modal-subtitle">
                Upload the "Polly User Access Level" workbook to refresh the roster
              </div>
            </div>
            <button class="close-btn" @click="closeApproverImport">✕</button>
          </div>

          <div class="modal-body approver-modal-body">
            <!-- current roster summary -->
            <div class="approver-current">
              <span v-if="approverRosterLoading">Loading the current roster...</span>
              <template v-else>
                <strong>{{ approverRosterCount }}</strong> active
                {{ approverRosterCount === 1 ? 'user' : 'users' }} on the roster
                <span v-if="approverLastImport" class="approver-current-sub">
                  — last import {{ formatDateTime(approverLastImport.committed_utc || approverLastImport.created_utc, true) }}
                  by {{ approverLastImport.uploaded_by_name || approverLastImport.uploaded_by_email || 'unknown' }}
                </span>
                <span v-else class="approver-current-sub">— never imported</span>
              </template>
            </div>

            <!-- Reason list. Polly owns this one, so it is a refresh, not an
                 edit surface. Lives here because this is the admin surface. -->
            <div class="approver-current">
              <span v-if="reasonsLoading">Loading the reason list...</span>
              <template v-else>
                <strong>{{ reasonCount }}</strong> active concession
                {{ reasonCount === 1 ? 'reason' : 'reasons' }}
                <span v-if="reasonLastSynced" class="approver-current-sub">
                  — last synced {{ formatDateTime(reasonLastSynced, true) }}
                </span>
                <span v-else class="approver-current-sub">— not synced yet</span>
                <button class="approver-toggle"
                        :disabled="reasonSyncing"
                        @click="syncConcessionReasons">
                  {{ reasonSyncing ? 'Refreshing...' : 'Refresh now' }}
                </button>
              </template>

              <div class="approver-table-note">
                Polly owns this list — it syncs on its own in the background. Refresh only if you
                changed something in Polly and want it reflected immediately.
              </div>

              <div v-if="reasonSyncMessage" class="approver-table-note">{{ reasonSyncMessage }}</div>

              <!-- A Polly type we could not map to branch/corp means that reason
                   is offered for BOTH tiers. Worth saying out loud. -->
              <div v-if="reasonUnmappedTypes.length" class="approver-table-note">
                Unrecognized Polly types: <strong>{{ reasonUnmappedTypes.join(', ') }}</strong>.
                Reasons with those types are offered for both branch and corp requests.
              </div>

              <div v-if="reasonSyncError" class="inline-error">{{ reasonSyncError }}</div>
            </div>

            <!-- file picker -->
            <div class="approver-drop"
                 :class="{ 'is-over': approverDragOver, 'has-file': !!approverFile }"
                 @dragover.prevent="approverDragOver = true"
                 @dragleave.prevent="approverDragOver = false"
                 @drop.prevent="onApproverDrop">
              <input ref="approverFileInput"
                     type="file"
                     accept=".xlsx,.xlsm"
                     class="approver-file-input"
                     @change="onApproverFilePicked" />

              <template v-if="approverFile">
                <i class="fa-duotone fa-file-excel approver-drop-icon"></i>
                <div class="approver-drop-title">{{ approverFile.name }}</div>
                <div class="approver-drop-hint">{{ formatFileSize(approverFile.size) }}</div>
              </template>
              <template v-else>
                <i class="fa-duotone fa-cloud-arrow-up approver-drop-icon"></i>
                <div class="approver-drop-title">Drop the .xlsx here</div>
                <div class="approver-drop-hint">or click to choose a file</div>
              </template>

              <button class="approver-browse-btn"
                      :disabled="approverBusy"
                      @click="$refs.approverFileInput.click()">
                {{ approverFile ? 'Choose a different file' : 'Choose file' }}
              </button>
            </div>

            <div v-if="approverError" class="inline-error">{{ approverError }}</div>

            <div v-if="approverBusy" class="approver-busy">
              <i class="fa-duotone fa-spinner fa-spin"></i>
              {{ approverCommitting ? 'Applying the import...' : 'Reading the workbook...' }}
            </div>

            <!-- preview -->
            <template v-if="approverPreview && !approverBusy">
              <div class="approver-result-banner"
                   :class="approverPreview.committed ? 'is-committed' : 'is-preview'">
                <i :class="approverPreview.committed ? 'fa-duotone fa-circle-check' : 'fa-duotone fa-eye'"></i>
                <span v-if="approverPreview.committed">
                  Import applied. The roster is now live.
                </span>
                <span v-else>
                  Preview only — nothing has been saved yet.
                </span>
              </div>

              <div class="approver-counts">
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_read }}</div>
                  <div class="approver-count-label">Rows read</div>
                </div>
                <div class="approver-count-card is-new">
                  <div class="approver-count-value">{{ approverPreview.rows_new }}</div>
                  <div class="approver-count-label">New</div>
                </div>
                <div class="approver-count-card is-changed">
                  <div class="approver-count-value">{{ approverPreview.rows_changed }}</div>
                  <div class="approver-count-label">Updated</div>
                </div>
                <div class="approver-count-card is-off">
                  <div class="approver-count-value">{{ approverPreview.rows_deactivating }}</div>
                  <div class="approver-count-label">Deactivated</div>
                </div>
                <div class="approver-count-card">
                  <div class="approver-count-value">{{ approverPreview.rows_unchanged }}</div>
                  <div class="approver-count-label">Unchanged</div>
                </div>
                <div class="approver-count-card" v-if="approverPreview.rows_skipped">
                  <div class="approver-count-value">{{ approverPreview.rows_skipped }}</div>
                  <div class="approver-count-label">Skipped</div>
                </div>
              </div>

              <!-- deactivation is the destructive part; call it out -->
              <div v-if="approverPreview.rows_deactivating > 0 && !approverPreview.committed"
                   class="approver-warn-banner">
                <i class="fa-duotone fa-triangle-exclamation"></i>
                <span>
                  {{ approverPreview.rows_deactivating }}
                  {{ approverPreview.rows_deactivating === 1 ? 'user is' : 'users are' }}
                  missing from this file and will be switched off. They are never deleted — a later
                  upload that includes them turns them back on.
                </span>
              </div>

              <!-- per-division rollup -->
              <div class="approver-section-title">By division</div>
              <div class="approver-table-wrap">
                <table class="approver-table">
                  <thead>
                    <tr>
                      <th>Division</th>
                      <th class="num">Users</th>
                      <th class="num">Branch approvers</th>
                      <th class="num">Corp approvers</th>
                      <th class="num">Ceilings set</th>
                      <th>Can request</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in approverPreview.divisions" :key="d.division">
                      <td>{{ d.division }}</td>
                      <td class="num">{{ d.user_count }}</td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.branch_approver_count === 0 }">
                        {{ d.branch_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.corp_approver_count === 0 }">
                        {{ d.corp_approver_count }}
                      </td>
                      <td class="num" :class="{ 'is-zero': d.can_request && d.threshold_cells_populated === 0 }">
                        {{ d.threshold_cells_populated }}
                      </td>
                      <td>
                        <span class="approver-pill" :class="d.can_request ? 'is-yes' : 'is-no'">
                          {{ d.can_request ? 'Yes' : 'Restricted' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="approver-table-note">
                A division with users but no approvers has nobody to send an over-threshold request to,
                so those land in this queue as concession requests. Zero ceilings set means every
                concession there auto-approves.
              </div>

              <!-- warnings -->
              <div v-if="approverPreview.warnings_total > 0">
                <div class="approver-section-title">
                  Warnings ({{ approverPreview.warnings_total }})
                  <button class="approver-toggle" @click="showApproverWarnings = !showApproverWarnings">
                    {{ showApproverWarnings ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverWarnings" class="approver-warn-list">
                  <div v-for="(w, i) in approverPreview.warnings" :key="i" class="approver-warn-row">
                    <span class="approver-warn-row-loc">Row {{ w.row }}</span>
                    <span class="approver-warn-row-who">{{ w.email || w.field }}</span>
                    <span class="approver-warn-row-msg">{{ w.message }}</span>
                  </div>
                  <div v-if="approverPreview.warnings_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.warnings.length }} of
                    {{ approverPreview.warnings_total }} warnings.
                  </div>
                </div>
                <div class="approver-table-note">
                  Warnings never block an import — they flag cells worth a second look in the workbook.
                </div>
              </div>

              <!-- change detail -->
              <div v-if="approverPreview.changes && approverPreview.changes.length">
                <div class="approver-section-title">
                  What changes
                  <button class="approver-toggle" @click="showApproverChanges = !showApproverChanges">
                    {{ showApproverChanges ? 'Hide' : 'Show' }}
                  </button>
                </div>
                <div v-if="showApproverChanges" class="approver-change-list">
                  <div v-for="(c, i) in approverPreview.changes" :key="i" class="approver-change-row">
                    <span class="approver-change-tag" :class="'tag-' + c.change_type">
                      {{ changeTypeLabel(c.change_type) }}
                    </span>
                    <span class="approver-change-who">
                      {{ c.name || c.email }}
                      <span class="approver-change-email" v-if="c.name">{{ c.email }}</span>
                    </span>
                    <span class="approver-change-div">{{ c.division || '—' }}</span>
                    <span class="approver-change-fields">
                      <span v-for="(f, fi) in c.fields" :key="fi" class="approver-change-field">
                        {{ f.field }}: <em>{{ f.from || 'empty' }}</em> → <strong>{{ f.to || 'empty' }}</strong>
                      </span>
                    </span>
                  </div>
                  <div v-if="approverPreview.changes_truncated" class="approver-warn-more">
                    Showing the first {{ approverPreview.changes.length }} rows. The counts above are complete.
                  </div>
                </div>
              </div>
            </template>

            <div class="modal-footer-actions">
              <button class="cancel-btn" :disabled="approverBusy" @click="closeApproverImport">
                {{ approverPreview && approverPreview.committed ? 'Done' : 'Cancel' }}
              </button>
              <button v-if="approverPreview && !approverPreview.committed"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="commitApproverImport">
                Apply import
              </button>
              <button v-else-if="!approverPreview"
                      class="save-btn"
                      :disabled="approverBusy || !approverFile"
                      @click="previewApproverImport">
                Preview changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="not-authorized-card">
      <div class="not-authorized-icon">
        <i class="fa-duotone fa-lock"></i>
      </div>
      <div class="not-authorized-title">Not authorized</div>
      <div class="not-authorized-text">You do not have access to Lock Desk Queue.</div>
    </div>
  </div>
</template>

<script>
  import api from '@/api/api.service'
  import auth from "@/authConfig.js"
  import signalRService from '@/api/signalr.service'
  import { readLockDeskQueuePrefs, writeLockDeskQueuePrefs } from '@/helpers/lockDeskQueuePrefs.js'
  import {
    Chart,
    ChartArea,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartTooltip
  } from "@progress/kendo-vue-charts"

  export default {
    name: 'LockDeskQueue',
    components: {
      Chart,
      ChartArea,
      ChartLegend,
      ChartSeries,
      ChartSeriesItem,
      ChartTooltip
    },
    data() {
      return {
        loading: false,
        errorMessage: '',
        rows: [],
        timeFrame: 'all',
        search: '',
        statusFilter: 'active',
        requestTypeFilter: '',
        assignedFilter: '',
        channelFilter: '',
        sortBy: 'arrived_date',
        sortDir: 'desc',
        page: 1,
        pageSize: 100,
        pollyOrgId: 698,
        showInsightsModal: false,
        insightsCopied: false,
        // Lock extension aggregates for the insights panel — fetched on modal open, time-frame scoped only
        extStats: null,
        extStatsLoading: false,
        extStatsError: '',
        insightFilters: {
          request_type: '',
          status: '',
          channel: '',
          orgid: '',
          assignee: ''
        },
        aiPrompt: '',
        aiMessages: [],
        aiLoading: false,
        aiError: '',
        showStatusModal: false,
        selectedStatusRow: {},
        pendingStatus: '',
        statusSaving: false,
        statusErrorMessage: '',
        showNotesModal: false,
        selectedNotesRow: {},
        notes: [],
        notesLoading: false,
        notesSaving: false,
        notesErrorMessage: '',
        newNoteText: '',
        // Compose scope for the notes panel: true = external (shared with the loan officer),
        // false = internal (lock-desk only). Defaults to external on revenue-alert items.
        composeExternal: false,
        // Send-a-note-to-recipients feature: chips of {name?, email} plus the typeahead search box.
        noteRecipients: [],
        recipientSearch: '',
        showRecipientDropdown: false,
        noteSending: false,
        // Per-note soft-delete: id awaiting inline confirmation, and id currently deleting.
        pendingDeleteNoteId: null,
        noteDeletingId: null,
        showAssignmentModal: false,
        selectedAssignmentRow: {},
        assignmentHistory: [],
        assignmentLoading: false,
        assignmentError: '',
        showCreateModal: false,
        createSaving: false,
        createErrorMessage: '',
        createForm: {
          request_type: '',
          request_description: '',
          loan_number: '',
          borrower_first: '',
          borrower_last: '',
          loan_officer_name: '',
          loan_officer_email: '',
          orgid: '',
          channel: ''
        },
        // Directory typeahead dropdown state for the create form's Loan Officer field.
        showLoDropdown: false,
        createMode: 'manual',
        aiText: '',
        aiImageFile: null,
        aiGenerating: false,
        aiError: '',
        aiGeneratedNote: '',
        aiPopulated: false,
        // Manager assign-to-user feature
        showAssignModal: false,
        selectedAssignRow: {},
        assignSearch: '',
        assignableUsers: [],      // persisted roster from the DB
        directoryUsers: [],       // org directory (GetUsers), lazy-loaded
        assignUsersLoading: false,
        assignSaving: false,
        assignError: '',
        showAddNewForm: false,
        newUser: { name: '', email: '' },
        _assignableLoaded: false,
        // Concession approver roster importer (managers only). The roster is
        // maintained in the "Polly User Access Level" workbook, not in this app.
        showApproverModal: false,
        approverFile: null,
        approverDragOver: false,
        approverPreview: null,   // the server's diff report, preview or applied
        approverError: '',
        approverBusy: false,
        approverCommitting: false,
        approverRosterCount: 0,
        approverRosterLoading: false,
        approverLastImport: null,
        showApproverWarnings: false,
        showApproverChanges: false,
        // Concession reason list — mirrored from Polly's price-exception reasons.
        reasonCount: 0,
        reasonLastSynced: null,
        reasonsLoading: false,
        reasonSyncing: false,
        reasonSyncMessage: '',
        reasonSyncError: '',
        reasonUnmappedTypes: []
      }
    },
    computed: {
      roles() {
        const raw = localStorage.getItem("active_user_roles") || localStorage.getItem("user_roles")
        if (!raw) return []
        try {
          const parsed = JSON.parse(raw)
          if (Array.isArray(parsed)) {
            return parsed.map(x => String(x || "").toLowerCase().trim()).filter(Boolean)
          }
          return String(parsed || "").split(",").map(x => String(x || "").toLowerCase().trim()).filter(Boolean)
        } catch {
          return String(raw || "").split(",").map(x => String(x || "").toLowerCase().trim()).filter(Boolean)
        }
      },
      isAuthorized() {
        return this.roles.includes("compensation admin") || this.roles.includes("lock desk queue manager")
      },
      // Managers can assign a request to a specific user (not just self-claim).
      isLockDeskManager() {
        return this.roles.includes("lock desk queue manager")
      },
      // The assign picker: persisted roster first, then org-directory people not
      // already on the roster (deduped by azure_id). Search filters both. The
      // logged-in user always appears so a manager can quickly assign to self too.
      filteredAssignUsers() {
        const q = (this.assignSearch || '').toLowerCase()
        const seen = new Set()
        const list = []

        const push = (name, email, azureId, source) => {
          const nm = (name || '').trim()
          if (!nm) return
          const azId = (azureId || '').toString().trim().toLowerCase()
          const dedupKey = azId || `name:${nm.toLowerCase()}`
          if (seen.has(dedupKey)) return
          seen.add(dedupKey)
          list.push({
            key: dedupKey,
            name: nm,
            email: (email || '').trim(),
            azure_id: azId,
            source
          })
        }

        for (const u of this.assignableUsers) push(u.name, u.email, u.azure_id, 'roster')
        for (const u of this.directoryUsers) push(u.name, u.email, u.azure_id, 'directory')

        const filtered = q
          ? list.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
          : list

        // Roster entries before directory-only ones; alphabetical within each group.
        return filtered.sort((a, b) => {
          if (a.source !== b.source) return a.source === 'roster' ? -1 : 1
          return a.name.localeCompare(b.name)
        })
      },
      // Directory typeahead matches for the create form's Loan Officer field.
      loSearchResults() {
        return this.matchUsers(this.createForm.loan_officer_name)
      },
      // Directory typeahead matches for the note-recipient picker, excluding already-added recipients.
      recipientMatches() {
        const chosen = new Set(this.noteRecipients.map(r => (r.email || '').toLowerCase()))
        return this.matchUsers(this.recipientSearch).filter(u => !u.email || !chosen.has(u.email.toLowerCase()))
      },
      currentAzureId() {
        const account = auth.user()
        if (account?.homeAccountId) return account.homeAccountId.split('.')[0].toLowerCase()
        return (
          localStorage.getItem('azure_id') ||
          localStorage.getItem('azureId') ||
          localStorage.getItem('active_user_azure_id') ||
          ''
        ).toString().trim().toLowerCase()
      },
      currentUserName() {
        const account = auth.user()
        if (account?.name) return account.name
        return (
          localStorage.getItem('full_name') ||
          localStorage.getItem('user_full_name') ||
          localStorage.getItem('display_name') ||
          localStorage.getItem('user_name') ||
          localStorage.getItem('name') ||
          'Me'
        ).toString().trim()
      },
      uniqueAssignees() {
        const seen = new Set()
        const list = []
        for (const row of this.rows) {
          if (!row.assigned_to_azure_id || !row.assigned_to_name) continue
          const id = String(row.assigned_to_azure_id).toLowerCase()
          if (seen.has(id)) continue
          seen.add(id)
          list.push({ id, name: row.assigned_to_name })
        }
        return list.sort((a, b) => a.name.localeCompare(b.name))
      },
      // Request types are dynamic — build the filter list from what's actually present in the data.
      // Known types keep a fixed order; anything new sorts in after them by its humanized label.
      requestTypeOptions() {
        const KNOWN_ORDER = ['lock_extension', 'reprice', 'float_down', 'other']
        const present = new Set()
        for (const row of this.rows) {
          present.add(String(row.request_type || 'other').trim().toLowerCase().replace(/[\s-]+/g, '_'))
        }
        const known = KNOWN_ORDER.filter(k => present.has(k))
        const dynamic = Array.from(present)
          .filter(k => k && !KNOWN_ORDER.includes(k))
          .sort((a, b) => this.requestTypeLabel(a).localeCompare(this.requestTypeLabel(b)))
        return [...known, ...dynamic].map(value => ({ value, label: this.requestTypeLabel(value) }))
      },
      // "By Request Type" cards, one per type actually present. Known business categories are always
      // shown (even at 0) with their familiar plural labels/icons; dynamic types each get their own
      // card before the catch-all "Other" — so nothing gets silently lumped into Other anymore.
      requestTypeCards() {
        const rows = this.filteredRows || []
        const norm = t => String(t || 'other').trim().toLowerCase().replace(/[\s-]+/g, '_')
        const counts = {}
        for (const r of rows) {
          const k = norm(r.request_type)
          counts[k] = (counts[k] || 0) + 1
        }
        const KNOWN = {
          lock_extension: { label: 'Extensions', icon: 'fa-calendar-plus' },
          reprice: { label: 'Reprices', icon: 'fa-arrows-rotate' },
          float_down: { label: 'Float Down', icon: 'fa-arrow-down-long' },
          revenue_alert: { label: 'Revenue Alerts', icon: 'fa-triangle-exclamation' },
          lock_request: { label: 'Lock Requests', icon: 'fa-lock' },
          other: { label: 'Other', icon: 'fa-ellipsis' }
        }
        // Only types actually present get a card. The three core ones used to be pinned on at 0,
        // which cost a card each for nothing — and with types now arriving from several producers
        // (revenue alerts, relocks, whatever comes next) the row has to earn its height.
        const CORE_ORDER = ['lock_extension', 'reprice', 'float_down']
        const present = Object.keys(counts).filter(k => counts[k] > 0)

        const core = CORE_ORDER.filter(k => present.includes(k))
        const dynamic = present
          .filter(k => !CORE_ORDER.includes(k) && k !== 'other')
          .sort((a, b) => this.requestTypeLabel(a).localeCompare(this.requestTypeLabel(b)))
        const trailing = present.includes('other') ? ['other'] : []

        return [...core, ...dynamic, ...trailing].map(value => ({
          value,
          label: KNOWN[value] ? KNOWN[value].label : this.requestTypeLabel(value),
          icon: KNOWN[value] ? KNOWN[value].icon : 'fa-tag',
          count: counts[value] || 0
        }))
      },
      // Dynamic legend of request types for the AI chat. Core types get a stable one-liner; ANY other
      // type (present or future) auto-documents from its humanized label plus a representative
      // request_description pulled from the data — so new types never require touching the AI prompt.
      requestTypeGlossary() {
        const norm = t => String(t || 'other').trim().toLowerCase().replace(/[\s-]+/g, '_')
        const CORE_DESC = {
          reprice: 'a rate reprice request',
          lock_extension: 'a lock term extension request',
          float_down: 'a float-down option request',
          revenue_alert: 'a loan whose funding forecast (CX.ID.BD.FORECAST) went negative and needs lock desk review',
          lock_request: 'a best-effort loan detected from a Polly event that needs lock desk review',
          other: 'a miscellaneous lock desk item'
        }
        const seen = new Map()
        for (const r of (this.rows || [])) {
          const key = norm(r.request_type)
          if (!seen.has(key)) seen.set(key, { label: this.requestTypeLabel(r.request_type), sample: '' })
          const entry = seen.get(key)
          const desc = (r.request_description || '').trim()
          // Prefer a descriptive example over terse placeholders like "Manual Review".
          if (desc && desc.toLowerCase() !== 'manual review' && (!entry.sample || entry.sample.length < desc.length)) {
            entry.sample = desc
          }
        }
        const lines = []
        for (const [key, { label, sample }] of seen) {
          let line = `- "${key}" (${label})`
          if (CORE_DESC[key]) line += ` = ${CORE_DESC[key]}`
          if (sample) line += ` — e.g. "${sample.slice(0, 180)}"`
          lines.push(line)
        }
        return lines.join('\n')
      },
      visibleRows() {
        return (this.rows || []).filter(x =>
          this.matchesTimeFrame(x.arrived_date || x.created_date || x.created_utc)
        )
      },
      // Status counts only. Per-type counts live in requestTypeCards, which derives them from the
      // types actually present — the hardcoded lock_extension/reprice/float_down/other tallies that
      // used to sit here were dead, and would have silently omitted every newer type anyway.
      summary() {
        const rows = this.filteredRows || []
        return {
          total: rows.length,
          not_started: rows.filter(x => !x.status || x.status === 'not_started').length,
          in_progress: rows.filter(x => x.status === 'in_progress').length,
          done: rows.filter(x => x.status === 'done').length
        }
      },
      channelOptions() {
        return [...new Set(
          (this.visibleRows || []).map(x => x.channel).filter(Boolean)
        )].sort((a, b) => a.localeCompare(b))
      },
      timeFrameLabel() {
        const map = {
          all: 'All time', today: 'Today', yesterday: 'Yesterday',
          this_week: 'This week', last_week: 'Last week', last_7_days: 'Last 7 days',
          last_30_days: 'Last 30 days', last_90_days: 'Last 90 days'
        }
        return map[this.timeFrame] || 'Current view'
      },
      // Rows feeding the insights modal — time-frame scoped, then sliced by the modal's own filters
      insightRows() {
        const f = this.insightFilters
        return (this.visibleRows || []).filter(r => {
          if (f.request_type && String(r.request_type || 'other').trim().toLowerCase().replace(/[\s-]+/g, '_') !== f.request_type) return false
          if (f.status && (r.status || 'not_started') !== f.status) return false
          if (f.channel && r.channel !== f.channel) return false
          if (f.orgid && r.orgid !== f.orgid) return false
          if (f.assignee) {
            if (f.assignee === 'unassigned') {
              if (r.assigned_to_azure_id || r.assigned_to_name) return false
            } else if (String(r.assigned_to_azure_id || '').toLowerCase() !== f.assignee) {
              return false
            }
          }
          return true
        })
      },
      // Market based vs at cost. Denominator is the CLASSIFIED subset, not every request —
      // requests that exit as exception_needed never get a type flag written (see ext-why note).
      extTypeSplit() {
        const s = this.extStats || {}
        const atCost = Number(s.at_cost || 0)
        const marketBased = Number(s.market_based || 0)
        const classified = atCost + marketBased
        const unknown = Number(s.type_unknown || 0)
        const total = Number(s.total_requests || 0)

        return {
          classified,
          unknown,
          unknownException: Number(s.type_unknown_exception || 0),
          unknownPct: total ? unknown / total * 100 : 0,
          bars: [
            { key: 'at_cost', label: 'At cost', value: atCost, pct: classified ? atCost / classified * 100 : 0, color: '#2a78d6' },
            { key: 'market_based', label: 'Market based', value: marketBased, pct: classified ? marketBased / classified * 100 : 0, color: '#eb6834' }
          ]
        }
      },
      // Auto-approved vs manual. Every request has a decision, so this one spans all of them.
      extDecisionSplit() {
        const s = this.extStats || {}
        const total = Number(s.total_requests || 0)
        const auto = Number(s.auto_approved || 0)
        const exception = Number(s.exception_needed || 0)
        const manual = Number(s.manual_review || 0)
        const other = Number(s.denied || 0) + Number(s.no_recommendation || 0)
        const human = exception + manual

        const detail = [
          exception ? `${exception} exception needed` : '',
          manual ? `${manual} manual review` : ''
        ].filter(Boolean).join(' · ')

        const bars = [
          { key: 'auto', label: 'Auto-approved', value: auto, pct: total ? auto / total * 100 : 0, color: '#4a3aa7', detail: '' },
          { key: 'human', label: 'Needs a human', value: human, pct: total ? human / total * 100 : 0, color: '#e87ba4', detail }
        ]
        // Only surfaced when non-zero so the segments always add up to the total.
        if (other) {
          bars.push({ key: 'other', label: 'Denied / no recommendation', value: other, pct: total ? other / total * 100 : 0, color: '#888780', detail: '' })
        }

        return { bars }
      },
      // Appended to Copy Summary so the pasted text carries the same two splits as the panel.
      // Denominators are spelled out because they differ between the two.
      extensionSummaryText() {
        const s = this.extStats
        if (!s || !s.total_requests) return ''

        const t = this.extTypeSplit
        const d = this.extDecisionSplit
        const out = [`\n\nLock extension requests — ${s.total_requests}${this.extRangeLabel ? ` (${this.extRangeLabel})` : ''}`]

        if (t.classified) {
          out.push(`• Extension type (of ${t.classified} classified): ` +
            t.bars.map(b => `${b.label} ${b.value} (${b.pct.toFixed(1)}%)`).join(', '))
        }
        if (t.unknown) {
          out.push(`• No type flag: ${t.unknown} (${t.unknownPct.toFixed(1)}% of all ${s.total_requests}) — ` +
            `${t.unknownException} exited as exception_needed before the pricing step that writes it.`)
        }
        out.push(`• AI decision (of ${s.total_requests}): ` +
          d.bars.map(b => `${b.label} ${b.value} (${b.pct.toFixed(1)}%)`).join(', '))

        return out.join('\n')
      },
      extRangeLabel() {
        const s = this.extStats
        if (!s || !s.first_request_utc || !s.last_request_utc) return ''
        const first = this.formatDateTime(s.first_request_utc)
        const last = this.formatDateTime(s.last_request_utc)
        if (first === '—' || last === '—') return ''
        return first === last ? first : `${first} – ${last}`
      },
      insightOrgOptions() {
        return [...new Set((this.visibleRows || []).map(x => x.orgid).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b))
      },
      insightFiltersActive() {
        const f = this.insightFilters
        return !!(f.request_type || f.status || f.channel || f.orgid || f.assignee)
      },
      statusChartData() {
        const r = this.insightRows
        const total = r.length || 1
        return [
          { label: 'Not Started', value: r.filter(x => (x.status || 'not_started') === 'not_started').length, color: '#9aa7c2' },
          { label: 'In Progress', value: r.filter(x => x.status === 'in_progress').length, color: '#f0b429' },
          { label: 'Done', value: r.filter(x => x.status === 'done').length, color: '#3fa66a' }
        ].filter(d => d.value > 0)
         .map(d => ({ ...d, label: `${d.label}  ${d.value} (${Math.round(d.value / total * 100)}%)` }))
      },
      typeChartData() {
        const total = this.insightRows.length || 1
        const counts = this._countBy(this.insightRows, r => this.requestTypeLabel(r.request_type))
        const palette = ['#2f6bd8', '#7048e8', '#16a394', '#e8843c', '#c2410c']
        return Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .map(([label, value], i) => ({
            label: `${label}  ${value} (${Math.round(value / total * 100)}%)`,
            value,
            color: palette[i % palette.length]
          }))
      },
      donutLabels() {
        return { visible: false }
      },
      // "Who's doing what" — top assignees with open vs done split
      assigneeChart() {
        const map = {}
        for (const r of this.insightRows) {
          const name = r.assigned_to_name || 'Unassigned'
          if (!map[name]) map[name] = { name, open: 0, done: 0 }
          if (r.status === 'done') map[name].done++
          else map[name].open++
        }
        const arr = Object.values(map)
          .sort((a, b) => (b.open + b.done) - (a.open + a.done))
          .slice(0, 8)
        return {
          categories: arr.map(x => x.name),
          open: arr.map(x => x.open),
          done: arr.map(x => x.done),
          hasData: arr.length > 0
        }
      },
      assigneeBarMax() {
        if (!this.assigneeChart.hasData) return 1
        const totals = this.assigneeChart.categories.map((_, i) =>
          this.assigneeChart.open[i] + this.assigneeChart.done[i]
        )
        return Math.max(...totals, 1)
      },
      // Stage averages across the filtered view (seconds; null when no data)
      stageAverages() {
        const avg = (vals) => {
          const f = vals.filter(s => s != null && s > 0)
          return f.length ? Math.round(f.reduce((a, b) => a + b, 0) / f.length) : null
        }
        const rows = this.insightRows
        return {
          wait: avg(rows.map(r => r.wait_seconds)),
          pickup: avg(rows.map(r => r.pickup_seconds)),
          turnaround: avg(rows.map(r => r.duration_seconds))
        }
      },
      // Avg handling (pickup→done) time per assignee — who works items fastest
      pickupByAssignee() {
        const map = {}
        for (const r of this.insightRows) {
          if (r.pickup_seconds == null || r.pickup_seconds <= 0) continue
          const name = r.assigned_to_name || 'Unassigned'
          if (!map[name]) map[name] = { name, total: 0, count: 0 }
          map[name].total += r.pickup_seconds
          map[name].count++
        }
        const arr = Object.values(map)
          .map(x => ({ name: x.name, avg: Math.round(x.total / x.count), count: x.count }))
          .sort((a, b) => b.avg - a.avg)
          .slice(0, 8)
        return {
          rows: arr,
          max: arr.length ? Math.max(...arr.map(x => x.avg), 1) : 1,
          hasData: arr.length > 0
        }
      },
      queueInsights() {
        const rows = this.insightRows || []
        const lines = []
        if (!rows.length) {
          lines.push({ icon: 'fa-circle-info', text: 'No items in the current view.' })
          return { lines, text: 'No items in the current view.' }
        }

        const open = rows.filter(r => (r.status || 'not_started') !== 'done')
        const done = rows.filter(r => r.status === 'done')
        const inProgress = rows.filter(r => r.status === 'in_progress')
        const pctDone = Math.round((done.length / rows.length) * 100)

        // Snapshot
        lines.push({
          icon: 'fa-table-rows',
          text: `${rows.length} item${rows.length === 1 ? '' : 's'} — ${pctDone}% complete (${done.length} done, ${open.length} open${inProgress.length ? `, ${inProgress.length} in progress` : ''}).`
        })

        // Request mix
        const typeTop = this._topEntries(this._countBy(rows, r => this.requestTypeLabel(r.request_type)), 3)
        if (typeTop.length) {
          lines.push({ icon: 'fa-tags', text: `Request mix: ${typeTop.map(([k, v]) => `${k} (${v})`).join(', ')}.` })
        }

        // Aging of open items
        const now = Date.now()
        const openAged = open
          .map(r => ({ r, d: this._toDate(r.arrived_date || r.created_date || r.created_utc) }))
          .filter(x => x.d)
        if (openAged.length) {
          openAged.sort((a, b) => a.d - b.d)
          const oldest = openAged[0]
          lines.push({
            icon: 'fa-hourglass-half',
            text: `Oldest open: loan ${oldest.r.loan_number || '—'} (${this.requestTypeLabel(oldest.r.request_type)}), waiting ${this._ago(now - oldest.d.getTime())}.`
          })
          const stale = openAged.filter(x => now - x.d.getTime() > 24 * 3600000).length
          if (stale) lines.push({ icon: 'fa-triangle-exclamation', text: `${stale} open item${stale === 1 ? '' : 's'} waiting over 24h.` })
        }

        // Throughput
        const startToday = new Date(); startToday.setHours(0, 0, 0, 0)
        const doneToday = done.filter(r => { const d = this._toDate(r.completed_utc); return d && d >= startToday }).length
        const durations = done.map(r => r.duration_seconds).filter(s => s != null && s > 0)
        let throughput = `Completed today: ${doneToday}.`
        if (durations.length) {
          const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
          throughput += ` Avg turnaround: ${this.formatDuration(avg)}.`
        }
        const waits = rows.map(r => r.wait_seconds).filter(s => s != null && s > 0)
        if (waits.length) {
          const avgWait = Math.round(waits.reduce((a, b) => a + b, 0) / waits.length)
          throughput += ` Avg wait to pickup: ${this.formatDuration(avgWait)}.`
        }
        const pickups = done.map(r => r.pickup_seconds).filter(s => s != null && s > 0)
        if (pickups.length) {
          const avgPickup = Math.round(pickups.reduce((a, b) => a + b, 0) / pickups.length)
          throughput += ` Avg pickup→done: ${this.formatDuration(avgPickup)}.`
        }
        lines.push({ icon: 'fa-gauge-high', text: throughput })

        // Workload
        const topAssignee = this._topEntries(this._countBy(open.filter(r => r.assigned_to_name), r => r.assigned_to_name), 1)[0]
        const unassignedOpen = open.filter(r => !r.assigned_to_azure_id && !r.assigned_to_name).length
        const wl = []
        if (topAssignee) wl.push(`heaviest load ${topAssignee[0]} (${topAssignee[1]} open)`)
        if (unassignedOpen) wl.push(`${unassignedOpen} unassigned`)
        if (wl.length) lines.push({ icon: 'fa-user-group', text: `Workload: ${wl.join(', ')}.` })

        // Channel / org mix
        const chTop = this._topEntries(this._countBy(rows.filter(r => r.channel), r => this.displayChannel(r.channel)), 1)[0]
        const orgTop = this._topEntries(this._countBy(rows.filter(r => r.orgid), r => r.orgid), 1)[0]
        const mix = []
        if (chTop) mix.push(`channel ${chTop[0]} (${chTop[1]})`)
        if (orgTop) mix.push(`org ${orgTop[0]} (${orgTop[1]})`)
        if (mix.length) lines.push({ icon: 'fa-layer-group', text: `Top ${mix.join(', ')}.` })

        const header = `Lock Desk Queue insights — ${this.timeFrameLabel}`
        const text = header + '\n' + lines.map(l => `• ${l.text}`).join('\n') + this.extensionSummaryText
        return { lines, text }
      },
      filteredRows() {
        let rows = [...this.visibleRows]

        if (this.search) {
          const term = this.search.toLowerCase()
          rows = rows.filter(x => {
            const haystack = [
              x.loan_number,
              x.borrower_name,
              x.borrower_first_name,
              x.borrower_last_name,
              x.orgid,
              x.loan_officer_name,
              x.loan_officer,
              x.channel,
              x.assigned_to_name,
              x.request_description,
              this.requestTypeLabel(x.request_type),
              this.statusLabel(x.status)
            ].filter(Boolean).join(' ').toLowerCase()
            return haystack.includes(term)
          })
        }

        if (this.statusFilter) {
          if (this.statusFilter === 'active') {
            rows = rows.filter(x => !x.status || x.status === 'not_started' || x.status === 'in_progress')
          } else if (this.statusFilter === 'not_started') {
            rows = rows.filter(x => !x.status || x.status === 'not_started')
          } else {
            rows = rows.filter(x => x.status === this.statusFilter)
          }
        }

        if (this.requestTypeFilter) {
          rows = rows.filter(x => String(x.request_type || 'other').trim().toLowerCase().replace(/[\s-]+/g, '_') === this.requestTypeFilter)
        }

        if (this.assignedFilter === 'mine') {
          rows = rows.filter(x =>
            String(x.assigned_to_azure_id || '').toLowerCase() === this.currentAzureId
          )
        } else if (this.assignedFilter === 'unassigned') {
          rows = rows.filter(x => !x.assigned_to_azure_id)
        } else if (this.assignedFilter) {
          rows = rows.filter(x =>
            String(x.assigned_to_azure_id || '').toLowerCase() === this.assignedFilter
          )
        }

        if (this.channelFilter) {
          rows = rows.filter(x => x.channel === this.channelFilter)
        }

        rows.sort((a, b) => {
          const aVal = this.sortValue(a, this.sortBy)
          const bVal = this.sortValue(b, this.sortBy)
          if (aVal === bVal) return 0
          if (aVal === null || aVal === undefined) return 1
          if (bVal === null || bVal === undefined) return -1
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return this.sortDir === 'asc' ? aVal - bVal : bVal - aVal
          }
          const result = String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true, sensitivity: 'base'
          })
          return this.sortDir === 'asc' ? result : -result
        })

        return rows
      },
      totalPages() {
        return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize))
      },
      pagedRows() {
        const start = (this.page - 1) * this.pageSize
        return this.filteredRows.slice(start, start + this.pageSize)
      },
      pageStart() {
        if (!this.filteredRows.length) return 0
        return (this.page - 1) * this.pageSize + 1
      },
      pageEnd() {
        return Math.min(this.page * this.pageSize, this.filteredRows.length)
      }
    },
    watch: {
      search()          { this.page = 1; this.savePrefs() },
      statusFilter()    { this.page = 1; this.savePrefs() },
      requestTypeFilter() { this.page = 1; this.savePrefs() },
      assignedFilter()  { this.page = 1; this.savePrefs() },
      channelFilter()   { this.page = 1; this.savePrefs() },
      timeFrame()       { this.page = 1; this.savePrefs(); this.loadData() },
      sortBy()          { this.savePrefs() },
      sortDir()         { this.savePrefs() },
      filteredRows() {
        if (this.page > this.totalPages) this.page = 1
      }
    },
    async mounted() {
      this.loadPrefs()
      if (!this.isAuthorized) return
      await this.loadData()
      this._startSignalR()
      // Safety-net auto-refresh: engine-processed reprices/extensions enter the queue via the
      // notify callback OR the dashboard sync — neither of which pushes to an already-open queue
      // page. Poll silently so newly-arrived items appear on their own (no manual Refresh needed).
      this._pollTimer = setInterval(() => {
        if (!document.hidden) this.loadData(true)
      }, 60000)
    },
    beforeUnmount() {
      if (this._onExtProcessed) signalRService.offLockExtensionQueueItemProcessed(this._onExtProcessed)
      if (this._onExtManual) signalRService.offLockExtensionManualStatusUpdated(this._onExtManual)
      if (this._onExtSubmitted) signalRService.offLockExtensionApprovedRequestSubmitted(this._onExtSubmitted)
      if (this._onQueueChanged) signalRService.offLockDeskQueueChanged(this._onQueueChanged)
      if (this._onRepriceProcessed) signalRService.offQueueItemProcessed(this._onRepriceProcessed)
      if (this._reloadTimer) clearTimeout(this._reloadTimer)
      if (this._pollTimer) clearInterval(this._pollTimer)
    },
    methods: {
      _debouncedReload() {
        if (this._reloadTimer) clearTimeout(this._reloadTimer)
        this._reloadTimer = setTimeout(() => { this.loadData(true) }, 600)
      },
      async _startSignalR() {
        // All SignalR-driven refreshes are SILENT — update rows in place, no loading screen.
        this._onExtProcessed = () => this.loadData(true)
        this._onExtManual = () => this.loadData(true)
        this._onExtSubmitted = () => this.loadData(true)
        this._onRepriceProcessed = () => this.loadData(true)
        // Queue mutations (status/assign/create/intake/note) by any user — debounced to coalesce bursts.
        // If a note arrived for the item whose Notes modal is open here, refresh that modal too.
        this._onQueueChanged = (dto) => {
          this._debouncedReload()
          const changedId = dto && (dto.itemId || dto.ItemId)
          if (this.showNotesModal && changedId && this.selectedNotesRow &&
              String(this.selectedNotesRow.id) === String(changedId)) {
            this.loadNotes()
          }
        }

        try {
          await signalRService.startLockExtensionConnection()
          signalRService.onLockExtensionQueueItemProcessed(this._onExtProcessed)
          signalRService.onLockExtensionManualStatusUpdated(this._onExtManual)
          signalRService.onLockExtensionApprovedRequestSubmitted(this._onExtSubmitted)
          signalRService.onLockDeskQueueChanged(this._onQueueChanged)
          signalRService.onLockExtensionHubReconnected(() => this.loadData(true))
        } catch { /* non-fatal — poll will cover it */ }

        try {
          await signalRService.startRepriceConnection()
          signalRService.onQueueItemProcessed(this._onRepriceProcessed)
          signalRService.onRepriceHubReconnected(() => this.loadData(true))
        } catch { /* non-fatal */ }
      },
      loadPrefs() {
        const prefs = readLockDeskQueuePrefs()
        this.timeFrame = prefs.timeFrame
        this.statusFilter = prefs.statusFilter
        this.requestTypeFilter = prefs.requestTypeFilter
        this.channelFilter = prefs.channelFilter
        this.assignedFilter = prefs.assignedFilter
        this.sortBy = prefs.sortBy
        this.sortDir = prefs.sortDir
      },
      savePrefs() {
        writeLockDeskQueuePrefs({
          timeFrame: this.timeFrame,
          statusFilter: this.statusFilter,
          requestTypeFilter: this.requestTypeFilter,
          channelFilter: this.channelFilter,
          assignedFilter: this.assignedFilter,
          sortBy: this.sortBy,
          sortDir: this.sortDir
        })
      },
      // silent=true refreshes the data WITHOUT toggling the full loading screen — used for
      // SignalR-driven updates so the table updates in place (no flicker) even with many users.
      async loadData(silent = false) {
        if (!silent) this.loading = true
        this.errorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const response = await api.GetLockDeskQueueItems(this.getRequestDays(), token)
          const data = Array.isArray(response?.data?.data)
            ? response.data.data
            : (Array.isArray(response?.data) ? response.data : [])
          const mapped = data.map(x => ({
            ...x,
            has_notes: !!x.has_notes,
            status_loading: false,
            status: x.status || 'not_started'
          }))
          this.rows = this._mergeRows(this._dedupeRows(mapped))
        } catch (e) {
          // On a silent refresh, keep the current rows rather than blanking the table on a transient error.
          if (!silent) {
            this.rows = []
            this.errorMessage = e?.response?.data?.message || e?.message || 'Failed to load lock desk queue.'
          }
        } finally {
          if (!silent) this.loading = false
        }
      },

      async _syncFromSources(token) {
        try {
          const EXT_NEEDS_MANUAL = ['manual_review', 'exception_needed', 'request_only', 'no_recommendation']
          const REPRICE_NEEDS_MANUAL = ['manual_review', 'request_only']

          const normalizeExtDecision = (row) => {
            const raw = row?.recommendation ?? row?.decision ?? row?.llm_decision ?? row?.review_result ?? ''
            const v = String(raw).trim().toLowerCase()
            if (v === 'approve' || v === 'approved') return 'approved'
            if (v === 'deny' || v === 'denied') return 'denied'
            if (v === 'manual_review' || v === 'manual review' || v === 'review') return 'manual_review'
            if (v === 'exception_needed' || v === 'exception needed' || v === 'exception') return 'exception_needed'
            if (v === 'request_only' || v === 'request only') return 'request_only'
            if (!v) return 'no_recommendation'
            return v
          }

          const normalizeRepriceDecision = (row) => row.decision || 'no_audit'

          // 1-day window: pending items are always recent; processed items are handled by source dashboards
          const SYNC_DAYS = 1

          const [extResponse, repriceResponse, queueResponse] = await Promise.all([
            api.GetLockExtensionsNeedingReview(SYNC_DAYS, token).catch(() => null),
            api.GetRepricedLocksNeedingReview(SYNC_DAYS, token).catch(() => null),
            api.GetLockDeskQueueItems(90, token).catch(() => null)
          ])

          const queueItems = Array.isArray(queueResponse?.data?.data)
            ? queueResponse.data.data
            : (Array.isArray(queueResponse?.data) ? queueResponse.data : [])

          // Track OPEN items only — a done ticket must not block re-adding a new one when a loan
          // needs another reprice/extension after the prior was solved out.
          const extMap = {}, repriceMap = {}
          for (const item of queueItems) {
            if (item.status === 'done') continue
            if (item.lock_extension_queue_id)
              extMap[String(item.lock_extension_queue_id).toLowerCase()] = true
            if (item.repriced_queue_id)
              repriceMap[String(item.repriced_queue_id).toLowerCase()] = true
          }

          const azureId = this.currentAzureId || ''
          const creates = []

          // Lock extensions — skip only terminal manual_status; SYNC_DAYS=1 prevents historical backfill
          const extRows = Array.isArray(extResponse?.data?.data)
            ? extResponse.data.data
            : (Array.isArray(extResponse?.data) ? extResponse.data : [])

          for (const row of extRows) {
            if (row.is_excluded) continue
            if (row.manual_status === 'submitted' || row.manual_status === 'denied') continue
            if (!EXT_NEEDS_MANUAL.includes(normalizeExtDecision(row))) continue
            const queueId = String(row.id || row.lock_extension_pending_approval_id || row.extension_queue_id || row.lock_extension_queue_id || '').toLowerCase()
            if (!queueId || extMap[queueId]) continue
            const decision = normalizeExtDecision(row)
            const label = { manual_review: 'Manual Review', exception_needed: 'Exception Needed', request_only: 'Request Only', no_recommendation: 'No Recommendation Yet' }[decision] || decision
            creates.push(api.CreateLockDeskQueueItem({
              request_type: 'lock_extension',
              loan_number: row.loan_number || '',
              borrower_first: row.borrower_first || row.borrower_first_name || '',
              borrower_last: row.borrower_last || row.borrower_last_name || '',
              loan_officer_name: row.loan_officer_name || row.loan_officer || '',
              orgid: row.orgid || row.org_id || '',
              channel: row.channel || '',
              request_description: label,
              created_by_azure_id: azureId,
              lock_extension_queue_id: queueId
            }, token).catch(() => null))
          }

          // Reprices — skip only terminal manual_status; SYNC_DAYS=1 prevents historical backfill
          const repriceRows = Array.isArray(repriceResponse?.data?.data)
            ? repriceResponse.data.data
            : (Array.isArray(repriceResponse?.data) ? repriceResponse.data : [])

          for (const row of repriceRows) {
            if (row.is_excluded) continue
            if (row.manual_status === 'approved' || row.manual_status === 'cleared') continue
            if (!REPRICE_NEEDS_MANUAL.includes(normalizeRepriceDecision(row))) continue
            const queueId = String(row.repriced_queue_id || '').toLowerCase()
            if (!queueId || repriceMap[queueId]) continue
            const decision = normalizeRepriceDecision(row)
            const label = { manual_review: 'Manual Review', request_only: 'Approve/Price Changed' }[decision] || decision
            creates.push(api.CreateLockDeskQueueItem({
              request_type: 'reprice',
              loan_number: row.loan_number || '',
              borrower_first: row.borrower_first_name || '',
              borrower_last: row.borrower_last_name || '',
              loan_officer_name: row.loan_officer_name || '',
              orgid: row.orgid || row.org_id || '',
              channel: row.channel || '',
              request_description: label,
              created_by_azure_id: azureId,
              repriced_queue_id: queueId
            }, token).catch(() => null))
          }

          if (creates.length) await Promise.all(creates)
        } catch {
          // Non-fatal — queue still loads with whatever is already there
        }
      },
      // Reconcile a freshly-fetched list into the current rows, REUSING existing row objects
      // (mutated in place) so unchanged rows aren't torn down/recreated. Keeps SignalR-driven
      // silent refreshes smooth — Vue only patches what actually changed. Order follows the server.
      _mergeRows(incoming) {
        const current = new Map((this.rows || []).map(r => [String(r.id), r]))
        return incoming.map(item => {
          const existing = current.get(String(item.id))
          if (!existing) return item
          const wasLoading = existing.status_loading
          Object.assign(existing, item)
          existing.status_loading = wasLoading || false
          return existing
        })
      },
      _dedupeRows(rows) {
        const STATUS_RANK = { in_progress: 2, not_started: 1, done: 0 }
        const keep = (a, b) => (STATUS_RANK[a.status] ?? 0) >= (STATUS_RANK[b.status] ?? 0) ? a : b
        const byExt = {}, byReprice = {}, seen = new Set()

        for (const row of rows) {
          if (row.lock_extension_queue_id) {
            const key = String(row.lock_extension_queue_id).toLowerCase()
            byExt[key] = byExt[key] ? keep(byExt[key], row) : row
          } else if (row.repriced_queue_id) {
            const key = String(row.repriced_queue_id).toLowerCase()
            byReprice[key] = byReprice[key] ? keep(byReprice[key], row) : row
          }
        }

        return rows.filter(row => {
          if (row.lock_extension_queue_id) {
            const key = String(row.lock_extension_queue_id).toLowerCase()
            if (byExt[key]?.id !== row.id) return false
          } else if (row.repriced_queue_id) {
            const key = String(row.repriced_queue_id).toLowerCase()
            if (byReprice[key]?.id !== row.id) return false
          }
          if (seen.has(row.id)) return false
          seen.add(row.id)
          return true
        })
      },
      borrowerName(row) {
        if (!row) return '—'
        if (row.borrower_name) return row.borrower_name
        const first = row.borrower_first_name || row.borrower_first || ''
        const last = row.borrower_last_name || row.borrower_last || ''
        return `${first} ${last}`.trim() || '—'
      },
      requestTypeLabel(type) {
        if (!type) return 'Other'
        // Canonicalize so "Lock Request", "lock request" and "lock_request" all map to one key.
        const key = String(type).trim().toLowerCase().replace(/[\s-]+/g, '_')
        const known = {
          lock_extension: 'Lock Extension',
          reprice: 'Reprice',
          float_down: 'Float Down',
          revenue_alert: 'Revenue Alert',
          lock_request: 'Lock Request',
          other: 'Other'
        }
        if (known[key]) return known[key]
        // Dynamic/unknown type: humanize the raw value (snake/kebab-case → Title Case).
        return key
          .replace(/[_-]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .replace(/\b\w/g, c => c.toUpperCase()) || 'Other'
      },
      requestTypeClass(type) {
        const key = String(type || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
        if (key === 'lock_extension') return 'type-extension'
        if (key === 'reprice') return 'type-reprice'
        if (key === 'float_down') return 'type-float'
        if (key === 'revenue_alert') return 'type-revenue'
        if (key === 'lock_request') return 'type-lock-request'
        // Everything else (including dynamic types) uses the neutral pill.
        return 'type-other'
      },
      openPolly(row) {
        if (!row?.loan_id) return
        const url = `https://lx.pollyex.com/pe/loans/${row.loan_id}?mode=lock&orgId=${this.pollyOrgId}`
        window.open(url, '_blank')
      },
      // Encompass on the web, opened straight to the loan. Same URL shape used
      // by Condition Command (ConditionsDashboardMain.openEncompassWeb) —
      // loan_id is the Encompass loan guid, the same field Polly is keyed on.
      openEncompass(row) {
        if (!row?.loan_id) return
        const url = `https://www.encompassloconnect.com/pipeline/${row.loan_id}`
        window.open(url, '_blank', 'noopener')
      },
      statusLabel(status) {
        if (status === 'in_progress') return 'In Progress'
        if (status === 'done') return 'Done'
        return 'Not Started'
      },
      statusClass(status) {
        if (status === 'in_progress') return 'status-in-progress'
        if (status === 'done') return 'status-done'
        return 'status-not-started'
      },
      displayChannel(value) {
        if (!value) return '—'
        const normalized = String(value).trim().toLowerCase()
        if (normalized === 'banked - wholesale' || normalized === 'banked-wholesale' || normalized === 'banked wholesale') return 'Wholesale'
        if (normalized === 'banked - retail' || normalized === 'banked-retail' || normalized === 'banked retail') return 'Retail'
        return value
      },
      setSort(key) {
        if (this.sortBy === key) {
          this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
        } else {
          this.sortBy = key
          this.sortDir = 'desc'
        }
      },
      sortIcon(key) {
        if (this.sortBy !== key) return '↕'
        return this.sortDir === 'asc' ? '↑' : '↓'
      },
      sortValue(row, key) {
        if (key === 'arrived_date') {
          return new Date(row.arrived_date || row.created_date || row.created_utc || 0).getTime()
        }
        if (key === 'borrower_last_name') return this.borrowerName(row)
        if (key === 'status') return row.status || ''
        if (key === 'has_notes') return row.has_notes ? 1 : 0
        if (key === 'request_type') return this.requestTypeLabel(row.request_type)
        if (key === 'assigned_to_name') return row.assigned_to_name || ''
        return row[key] ?? ''
      },
      prevPage() {
        if (this.page > 1) this.page--
      },
      nextPage() {
        if (this.page < this.totalPages) this.page++
      },
      getRequestDays() {
        if (this.timeFrame === 'all') return 90
        if (this.timeFrame === 'today') return 2
        if (this.timeFrame === 'yesterday') return 2
        if (this.timeFrame === 'this_week') return 7
        if (this.timeFrame === 'last_week') return 14
        if (this.timeFrame === 'last_7_days') return 7
        if (this.timeFrame === 'last_30_days') return 30
        if (this.timeFrame === 'last_90_days') return 90
        return 90
      },
      startOfDay(date) {
        const d = new Date(date)
        d.setHours(0, 0, 0, 0)
        return d
      },
      endOfDay(date) {
        const d = new Date(date)
        d.setHours(23, 59, 59, 999)
        return d
      },
      startOfWeek(date) {
        const d = this.startOfDay(date)
        const day = d.getDay()
        const diff = day === 0 ? -6 : 1 - day
        d.setDate(d.getDate() + diff)
        return d
      },
      endOfWeek(date) {
        const d = this.startOfWeek(date)
        d.setDate(d.getDate() + 6)
        d.setHours(23, 59, 59, 999)
        return d
      },
      matchesTimeFrame(value) {
        if (this.timeFrame === 'all') return true
        if (!value) return false
        const rowDate = new Date(value)
        if (Number.isNaN(rowDate.getTime())) return false
        const now = new Date()
        const todayStart = this.startOfDay(now)
        const todayEnd = this.endOfDay(now)
        if (this.timeFrame === 'today') return rowDate >= todayStart && rowDate <= todayEnd
        if (this.timeFrame === 'yesterday') {
          const yesterday = new Date(todayStart)
          yesterday.setDate(yesterday.getDate() - 1)
          return rowDate >= this.startOfDay(yesterday) && rowDate <= this.endOfDay(yesterday)
        }
        if (this.timeFrame === 'this_week') return rowDate >= this.startOfWeek(now) && rowDate <= this.endOfWeek(now)
        if (this.timeFrame === 'last_week') {
          const thisWeekStart = this.startOfWeek(now)
          const lastWeekStart = new Date(thisWeekStart)
          lastWeekStart.setDate(lastWeekStart.getDate() - 7)
          const lastWeekEnd = new Date(thisWeekStart)
          lastWeekEnd.setMilliseconds(-1)
          return rowDate >= lastWeekStart && rowDate <= lastWeekEnd
        }
        if (this.timeFrame === 'last_7_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 6)
          return rowDate >= start && rowDate <= todayEnd
        }
        if (this.timeFrame === 'last_30_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 29)
          return rowDate >= start && rowDate <= todayEnd
        }
        if (this.timeFrame === 'last_90_days') {
          const start = this.startOfDay(now)
          start.setDate(start.getDate() - 89)
          return rowDate >= start && rowDate <= todayEnd
        }
        return true
      },
      formatDateTime(value, withTime = false) {
        if (!value) return '—'
        const str = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+') ? value + 'Z' : value
        const date = new Date(str)
        if (Number.isNaN(date.getTime())) return '—'
        if (withTime) return date.toLocaleDateString('en-US') + ' ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        return date.toLocaleDateString('en-US')
      },
      formatNoteDateTime(value) {
        const date = this._toDate(value)
        if (!date) return ''
        return date.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      },
      openStatusModal(row) {
        this.selectedStatusRow = row
        this.pendingStatus = row.status || 'not_started'
        this.statusErrorMessage = ''
        this.showStatusModal = true
      },
      closeStatusModal() {
        this.showStatusModal = false
        this.selectedStatusRow = {}
        this.pendingStatus = ''
        this.statusErrorMessage = ''
      },
      async submitStatus() {
        const row = this.selectedStatusRow
        if (!row?.id) return
        const newStatus = this.pendingStatus
        if (newStatus === (row.status || 'not_started')) return

        this.statusSaving = true
        this.statusErrorMessage = ''
        row.status_loading = true

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const assignOnStatus = newStatus === 'in_progress' || newStatus === 'done'

          await api.UpdateLockDeskQueueItemStatus({
            id: row.id,
            status: newStatus,
            updated_by_azure_id: this.currentAzureId,
            updated_by_name: this.currentUserName,
          }, tokenResponse.accessToken)

          // Setting in-progress/done auto-assigns the item to the acting user — whoever works it owns it.
          if (assignOnStatus) {
            try {
              await api.AssignLockDeskQueueItem({
                id: row.id,
                assigned_to_azure_id: this.currentAzureId,
                assigned_to_name: this.currentUserName,
                assigned_by_azure_id: this.currentAzureId,
                assigned_by_name: this.currentUserName
              }, tokenResponse.accessToken)
              // First pickup — stamp assigned_utc + wait time locally (server stamps the authoritative value)
              if (!row.assigned_utc) {
                row.assigned_utc = new Date().toISOString()
                const arr = this._toDate(row.arrived_date || row.created_date || row.created_utc)
                if (arr) row.wait_seconds = Math.round((new Date() - arr) / 1000)
              }
              // Taking the assignment starts the work — stamp started_* if not already set
              if (!row.started_by_name) {
                row.started_by_name = this.currentUserName
                row.started_by_azure_id = this.currentAzureId
              }
              if (!row.started_utc) row.started_utc = row.assigned_utc
              row.assigned_to_azure_id = this.currentAzureId
              row.assigned_to_name = this.currentUserName
            } catch { /* non-fatal */ }
          }

          // Update local tracking fields immediately so the tracking columns reflect the change
          if (newStatus === 'in_progress' && !row.started_by_name) {
            row.started_by_name = this.currentUserName
            row.started_by_azure_id = this.currentAzureId
            row.started_utc = new Date().toISOString()
          }
          if (newStatus === 'done') {
            row.completed_by_name = this.currentUserName
            row.completed_by_azure_id = this.currentAzureId
            row.completed_utc = new Date().toISOString()
            if (!row.credited_by_name) row.credited_by_name = row.started_by_name || this.currentUserName
            // Backfill Started By/At if it was completed directly without an In Progress step
            if (!row.started_by_name) {
              row.started_by_name = this.currentUserName
              row.started_by_azure_id = this.currentAzureId
            }
            if (!row.started_utc) row.started_utc = row.assigned_utc || new Date().toISOString()
            // Turnaround: time from arrival to completion (always computable for done items)
            const arrived = this._toDate(row.arrived_date || row.created_date || row.created_utc)
            if (arrived) {
              row.duration_seconds = Math.round((new Date() - arrived) / 1000)
            }
            // Pickup: time from assignment to completion (only when it was assigned); floor at 0
            const assigned = this._toDate(row.assigned_utc)
            if (assigned) {
              row.pickup_seconds = Math.max(0, Math.round((new Date() - assigned) / 1000))
            }
          }

          row.status = newStatus
          this.closeStatusModal()
        } catch (e) {
          this.statusErrorMessage = e?.response?.data?.message || e?.message || 'Failed to update status.'
        } finally {
          this.statusSaving = false
          row.status_loading = false
        }
      },
      // ── Manager: assign to a specific user ──────────────────────────────────
      userInitials(name) {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean)
        if (!parts.length) return '?'
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      },
      isRowAssignee(u) {
        const rowId = (this.selectedAssignRow.assigned_to_azure_id || '').toString().toLowerCase()
        return !!rowId && !!u.azure_id && rowId === u.azure_id
      },
      async openAssignModal(row) {
        if (!row?.id) return
        this.selectedAssignRow = row
        this.assignSearch = ''
        this.assignError = ''
        this.showAddNewForm = false
        this.newUser = { name: '', email: '' }
        this.showAssignModal = true
        this.$nextTick(() => { this.$refs.assignSearchInput?.focus() })
        await this.loadAssignableUsers()
      },
      closeAssignModal() {
        this.showAssignModal = false
        this.selectedAssignRow = {}
        this.assignSearch = ''
        this.assignError = ''
        this.showAddNewForm = false
        this.newUser = { name: '', email: '' }
      },
      // Load the persisted roster + the org directory once, then reuse. Directory
      // is best-effort — if it fails the roster still works.
      async loadAssignableUsers() {
        if (this._assignableLoaded) return
        this.assignUsersLoading = true
        this.assignError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const [rosterRes, dirRes] = await Promise.allSettled([
            api.GetLockDeskAssignableUsers(token),
            api.GetUsers(token)
          ])

          if (rosterRes.status === 'fulfilled') {
            const data = rosterRes.value?.data?.data || rosterRes.value?.data || []
            this.assignableUsers = data.map(u => ({
              id: u.id,
              name: u.name,
              email: u.email || '',
              azure_id: (u.azure_id || '').toString()
            }))
            // Only cache-lock once the roster (the important half) actually loaded,
            // so a transient failure retries the next time the modal opens.
            this._assignableLoaded = true
          } else {
            this.assignError = 'Could not load the saved user list.'
          }

          if (dirRes.status === 'fulfilled') {
            const data = dirRes.value?.data?.data || dirRes.value?.data || []
            this.directoryUsers = data
              .filter(u => u && (u.displayName || u.name))
              .map(u => ({
                name: u.displayName || u.name,
                email: u.email || u.mail || '',
                azure_id: (u.id || u.azureId || u.azure_id || '').toString()
              }))
          }
        } catch (e) {
          this.assignError = e?.response?.data?.message || e?.message || 'Failed to load users.'
        } finally {
          this.assignUsersLoading = false
        }
      },
      // Assign the current row to a picked user. A directory-sourced pick is also
      // persisted to the roster (best-effort) so it's one click next time.
      async assignToUser(user) {
        const row = this.selectedAssignRow
        if (!row?.id || this.assignSaving) return
        this.assignSaving = true
        this.assignError = ''
        row.status_loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          await api.AssignLockDeskQueueItem({
            id: row.id,
            assigned_to_azure_id: user.azure_id || '',
            assigned_to_name: user.name,
            assigned_by_azure_id: this.currentAzureId,
            assigned_by_name: this.currentUserName
          }, token)

          // A directory user assigned for the first time joins the shared roster.
          if (user.source === 'directory') {
            this._persistRosterUser(user, token)
          }

          this._applyAssignmentLocally(row, user.azure_id || '', user.name)
          this.closeAssignModal()
        } catch (e) {
          this.assignError = e?.response?.data?.message || e?.message || 'Failed to assign.'
        } finally {
          this.assignSaving = false
          row.status_loading = false
        }
      },
      openAddNewForm() {
        // Seed the name from whatever they've typed — the common case is
        // "searched, not found, add them".
        this.newUser = { name: this.assignSearch || '', email: '' }
        this.showAddNewForm = true
      },
      cancelAddNewForm() {
        this.showAddNewForm = false
        this.newUser = { name: '', email: '' }
      },
      async saveAndAssignNewUser() {
        const row = this.selectedAssignRow
        if (!row?.id || !this.newUser.name || this.assignSaving) return
        this.assignSaving = true
        this.assignError = ''
        row.status_loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          // Persist to the roster first so we get back the saved record (with any
          // server-side de-dupe applied), then assign the row to them.
          const res = await api.UpsertLockDeskAssignableUser({
            name: this.newUser.name,
            email: this.newUser.email || null,
            azure_id: null,
            created_by_azure_id: this.currentAzureId,
            created_by_name: this.currentUserName
          }, token)
          const saved = res?.data?.data || res?.data || {}
          const savedName = saved.name || this.newUser.name
          const savedAzureId = (saved.azure_id || '').toString()

          // Reflect it in the local roster immediately.
          if (saved.id && !this.assignableUsers.some(u => u.id === saved.id)) {
            this.assignableUsers.push({
              id: saved.id,
              name: savedName,
              email: saved.email || this.newUser.email || '',
              azure_id: savedAzureId
            })
          }

          await api.AssignLockDeskQueueItem({
            id: row.id,
            assigned_to_azure_id: savedAzureId,
            assigned_to_name: savedName,
            assigned_by_azure_id: this.currentAzureId,
            assigned_by_name: this.currentUserName
          }, token)

          this._applyAssignmentLocally(row, savedAzureId, savedName)
          this.closeAssignModal()
        } catch (e) {
          this.assignError = e?.response?.data?.message || e?.message || 'Failed to save user.'
        } finally {
          this.assignSaving = false
          row.status_loading = false
        }
      },
      async unassignRow() {
        const row = this.selectedAssignRow
        if (!row?.id || this.assignSaving) return
        this.assignSaving = true
        this.assignError = ''
        row.status_loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          // Send null (not '') so the backend clears the owner cleanly — an empty
          // string is treated as a real assignee (logs an event + bumps the
          // reassignment count); null skips the audit log and leaves timings intact.
          await api.AssignLockDeskQueueItem({
            id: row.id,
            assigned_to_azure_id: null,
            assigned_to_name: null,
            assigned_by_azure_id: this.currentAzureId,
            assigned_by_name: this.currentUserName
          }, tokenResponse.accessToken)
          row.assigned_to_azure_id = ''
          row.assigned_to_name = ''
          this.closeAssignModal()
        } catch (e) {
          this.assignError = e?.response?.data?.message || e?.message || 'Failed to unassign.'
        } finally {
          this.assignSaving = false
          row.status_loading = false
        }
      },
      // Best-effort roster write for a directory pick — never blocks the assignment.
      async _persistRosterUser(user, token) {
        try {
          const res = await api.UpsertLockDeskAssignableUser({
            name: user.name,
            email: user.email || null,
            azure_id: user.azure_id || null,
            created_by_azure_id: this.currentAzureId,
            created_by_name: this.currentUserName
          }, token)
          const saved = res?.data?.data
          if (saved?.id && !this.assignableUsers.some(u => u.id === saved.id)) {
            this.assignableUsers.push({
              id: saved.id,
              name: saved.name || user.name,
              email: saved.email || user.email || '',
              azure_id: (saved.azure_id || user.azure_id || '').toString()
            })
          }
        } catch { /* roster caching is non-critical */ }
      },
      // Mirror the row's assignment fields locally so the table updates without a
      // full reload (same first-pickup/started stamping as claimItem).
      _applyAssignmentLocally(row, azureId, name) {
        if (row.assigned_to_azure_id && row.assigned_to_azure_id !== azureId) {
          row.reassignment_count = (row.reassignment_count || 0) + 1
        }
        if (!row.assigned_utc) {
          row.assigned_utc = new Date().toISOString()
          const arr = this._toDate(row.arrived_date || row.created_date || row.created_utc)
          if (arr) row.wait_seconds = Math.round((new Date() - arr) / 1000)
        }
        if (!row.started_by_name) {
          row.started_by_name = name
          row.started_by_azure_id = azureId
        }
        if (!row.started_utc) row.started_utc = row.assigned_utc
        row.assigned_to_azure_id = azureId
        row.assigned_to_name = name
      },
      async claimItem(row) {
        if (!row?.id) return
        row.status_loading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.AssignLockDeskQueueItem({
            id: row.id,
            assigned_to_azure_id: this.currentAzureId,
            assigned_to_name: this.currentUserName,
            assigned_by_azure_id: this.currentAzureId,
            assigned_by_name: this.currentUserName
          }, tokenResponse.accessToken)
          // If it was already someone else's, this is a reassignment — reflect it locally
          if (row.assigned_to_azure_id && row.assigned_to_azure_id !== this.currentAzureId) {
            row.reassignment_count = (row.reassignment_count || 0) + 1
          }
          // First pickup — stamp assigned_utc + wait time locally (server stamps the authoritative value)
          if (!row.assigned_utc) {
            row.assigned_utc = new Date().toISOString()
            const arr = this._toDate(row.arrived_date || row.created_date || row.created_utc)
            if (arr) row.wait_seconds = Math.round((new Date() - arr) / 1000)
          }
          // Taking the assignment starts the work — stamp started_* if not already set
          if (!row.started_by_name) {
            row.started_by_name = this.currentUserName
            row.started_by_azure_id = this.currentAzureId
          }
          if (!row.started_utc) row.started_utc = row.assigned_utc
          row.assigned_to_azure_id = this.currentAzureId
          row.assigned_to_name = this.currentUserName
        } catch (e) {
          console.error('Failed to claim queue item', e)
        } finally {
          row.status_loading = false
        }
      },
      async openAssignmentHistory(row) {
        this.selectedAssignmentRow = row
        this.assignmentHistory = []
        this.assignmentError = ''
        this.assignmentLoading = true
        this.showAssignmentModal = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetLockDeskQueueAssignments(row.id, tokenResponse.accessToken)
          this.assignmentHistory = res?.data?.data || res?.data || []
        } catch (e) {
          this.assignmentError = e?.response?.data?.message || e?.message || 'Failed to load assignment history.'
        } finally {
          this.assignmentLoading = false
        }
      },
      closeAssignmentHistory() {
        this.showAssignmentModal = false
        this.selectedAssignmentRow = {}
        this.assignmentHistory = []
        this.assignmentError = ''
      },
      async openNotesModal(row) {
        this.selectedNotesRow = row
        this.notes = []
        this.notesErrorMessage = ''
        this.newNoteText = ''
        // Revenue-alert notes are external by default; everything else starts internal.
        this.composeExternal = this.isRevenueAlert(row)
        this.noteRecipients = []
        this.recipientSearch = ''
        this.showRecipientDropdown = false
        this.pendingDeleteNoteId = null
        this.noteDeletingId = null
        this.showNotesModal = true
        // Preload roster + directory (best-effort) so the recipient typeahead has data.
        this.loadAssignableUsers()
        await this.loadNotes()
      },
      closeNotesModal() {
        this.showNotesModal = false
        this.selectedNotesRow = {}
        this.notes = []
        this.notesErrorMessage = ''
        this.newNoteText = ''
        this.noteRecipients = []
        this.recipientSearch = ''
        this.showRecipientDropdown = false
        this.pendingDeleteNoteId = null
        this.noteDeletingId = null
      },

      openCreateModal() {
        this.createForm = {
          request_type: '',
          request_description: '',
          loan_number: '',
          borrower_first: '',
          borrower_last: '',
          loan_officer_name: '',
          loan_officer_email: '',
          orgid: '',
          channel: ''
        }
        this.showLoDropdown = false
        this.createErrorMessage = ''
        this.createMode = 'manual'
        this.aiText = ''
        this.aiImageFile = null
        this.aiGenerating = false
        this.aiError = ''
        this.aiGeneratedNote = ''
        this.aiPopulated = false
        this.showCreateModal = true
        // Preload roster + directory (best-effort) so the LO typeahead has data.
        this.loadAssignableUsers()
      },
      closeCreateModal() {
        this.showCreateModal = false
        this.showLoDropdown = false
        this.createErrorMessage = ''
        this.createMode = 'manual'
        this.aiText = ''
        this.aiImageFile = null
        this.aiGenerating = false
        this.aiError = ''
        this.aiGeneratedNote = ''
        this.aiPopulated = false
      },
      async submitCreateItem() {
        if (!this.createForm.request_type) {
          this.createErrorMessage = 'Request type is required.'
          return
        }
        this.createSaving = true
        this.createErrorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.CreateLockDeskQueueItem(
            { ...this.createForm, created_by_azure_id: this.currentAzureId },
            tokenResponse.accessToken
          )
          const newItem = res?.data?.data || res?.data
          if (newItem) {
            const row = { ...newItem, has_notes: false, status_loading: false, status: newItem.status || 'not_started' }
            this.rows.unshift(row)
            if (this.aiGeneratedNote && newItem.id) {
              try {
                await api.SubmitLockDeskQueueNote({
                  item_id: newItem.id,
                  note: this.aiGeneratedNote,
                  submitted_by: this.currentUserName,
                  submitted_by_azure_id: this.currentAzureId,
                  is_external: this.isRevenueAlert(newItem)
                }, tokenResponse.accessToken)
                row.has_notes = true
              } catch {
                // Note submission failure is non-fatal
              }
            }
          }
          this.closeCreateModal()
        } catch (e) {
          this.createErrorMessage = e?.response?.data?.message || e?.message || 'Failed to create request.'
        } finally {
          this.createSaving = false
        }
      },
      onAiImageSelect(event) {
        const file = event.target.files[0]
        if (file) this.aiImageFile = file
      },
      onAiPaste(event) {
        const items = event.clipboardData?.items
        if (!items) return
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.startsWith('image/')) {
            const file = items[i].getAsFile()
            if (file) {
              this.aiImageFile = new File([file], 'pasted-screenshot.png', { type: file.type || 'image/png' })
              event.preventDefault()
              break
            }
          }
        }
      },
      fileToBase64(file) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (e) => resolve(e.target.result.split(',')[1])
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
      },
      async generateFromAI() {
        if (!this.aiText && !this.aiImageFile) return
        this.aiGenerating = true
        this.aiError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          let image_base64 = null
          let image_media_type = 'image/png'
          if (this.aiImageFile) {
            image_base64 = await this.fileToBase64(this.aiImageFile)
            image_media_type = this.aiImageFile.type || 'image/png'
          }
          const res = await api.GenerateLockDeskQueueItem({
            text_content: this.aiText || null,
            image_base64,
            image_media_type
          }, tokenResponse.accessToken)
          const result = res?.data?.data || res?.data
          if (!result) throw new Error('No result from AI.')
          this.createForm.request_type = result.request_type || 'other'
          this.createForm.loan_number = result.loan_number || ''
          this.createForm.borrower_first = result.borrower_first || ''
          this.createForm.borrower_last = result.borrower_last || ''
          this.createForm.loan_officer_name = result.loan_officer_name || ''
          this.createForm.orgid = result.orgid || ''
          this.createForm.channel = result.channel || ''
          this.createForm.request_description = result.request_description || ''
          this.aiGeneratedNote = result.notes_text || ''
          this.aiPopulated = true
          this.createMode = 'manual'
        } catch (e) {
          this.aiError = e?.response?.data?.message || e?.message || 'AI generation failed.'
        } finally {
          this.aiGenerating = false
        }
      },
      async loadNotes() {
        const itemId = this.selectedNotesRow?.id || this.selectedNotesRow?.loan_id
        if (!itemId) return

        this.notesLoading = true
        this.notesErrorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const response = await api.GetLockDeskQueueNotes(itemId, tokenResponse.accessToken)
          this.notes = Array.isArray(response?.data?.data)
            ? response.data.data
            : (Array.isArray(response?.data) ? response.data : [])
        } catch (e) {
          this.notes = []
          this.notesErrorMessage = e?.response?.data?.message || e?.message || 'Failed to load notes.'
        } finally {
          this.notesLoading = false
        }
      },
      async submitNote() {
        if (!this.newNoteText) return
        const row = this.selectedNotesRow
        if (!row?.id && !row?.loan_id) return

        this.notesSaving = true
        this.notesErrorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.SubmitLockDeskQueueNote({
            item_id: row.id,
            loan_id: row.loan_id,
            note: this.newNoteText,
            submitted_by: this.currentUserName,
            submitted_by_azure_id: this.currentAzureId,
            created_by: this.currentAzureId || this.currentUserName,
            message_type: 'LockDeskQueue',
            is_external: this.composeExternal
          }, tokenResponse.accessToken)
          this.newNoteText = ''
          row.has_notes = true
          await this.loadNotes()
        } catch (e) {
          this.notesErrorMessage = e?.response?.data?.message || e?.message || 'Failed to submit note.'
        } finally {
          this.notesSaving = false
        }
      },
      getNoteText(note) {
        return note?.note || note?.notes || note?.message || note?.text || note?.note_text || note?.body || ''
      },
      getNoteCreatedDate(note) {
        return note?.created || note?.created_date || note?.createdDate || note?.created_utc || note?.updated_date || null
      },
      getNoteAuthor(note) {
        return (
          note?.submitted_by ||
          note?.created_by_name || note?.createdByName ||
          note?.created_by || note?.createdBy ||
          note?.user_name || note?.author ||
          'Lock Desk Note'
        )
      },
      // True when a note is flagged external (shared with the loan officer). Tolerates bool/number/string.
      getNoteIsExternal(note) {
        const v = note?.is_external ?? note?.isExternal
        return v === true || v === 1 || v === '1' || v === 'true'
      },
      // Canonicalize request_type the same way requestTypeLabel/Class do, then test for revenue alert.
      isRevenueAlert(row) {
        const key = String(row?.request_type || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
        return key === 'revenue_alert'
      },
      // Shared directory search over the roster + org directory (same source as assign/reassign).
      // Returns up to `limit` matches as {key, name, email, azure_id, source}.
      matchUsers(query, limit = 8) {
        const q = (query || '').toLowerCase().trim()
        if (!q) return []
        const seen = new Set()
        const list = []
        const push = (name, email, azureId, source) => {
          const nm = (name || '').trim()
          if (!nm) return
          const azId = (azureId || '').toString().trim().toLowerCase()
          const key = azId || `name:${nm.toLowerCase()}`
          if (seen.has(key)) return
          seen.add(key)
          list.push({ key, name: nm, email: (email || '').trim(), azure_id: azId, source })
        }
        for (const u of this.assignableUsers) push(u.name, u.email, u.azure_id, 'roster')
        for (const u of this.directoryUsers) push(u.name, u.email, u.azure_id, 'directory')
        return list
          .filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
          .sort((a, b) => (a.source !== b.source ? (a.source === 'roster' ? -1 : 1) : a.name.localeCompare(b.name)))
          .slice(0, limit)
      },
      basicEmailValid(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim())
      },
      // ── Create form: Loan Officer directory picker ──────────────────────────
      selectLoUser(u) {
        this.createForm.loan_officer_name = u.name
        if (u.email) this.createForm.loan_officer_email = u.email
        this.showLoDropdown = false
      },
      hideLoDropdownSoon() {
        // Delay so a click on a dropdown row registers before it hides on blur.
        setTimeout(() => { this.showLoDropdown = false }, 150)
      },
      hideRecipientDropdownSoon() {
        setTimeout(() => { this.showRecipientDropdown = false }, 150)
      },
      // ── Notes: recipient picker + send ──────────────────────────────────────
      addRecipientUser(u) {
        if (!u?.email) return
        const email = u.email.trim()
        if (!this.noteRecipients.some(r => (r.email || '').toLowerCase() === email.toLowerCase())) {
          this.noteRecipients.push({ name: u.name || '', email })
        }
        this.recipientSearch = ''
        this.showRecipientDropdown = false
      },
      addRecipientEmail() {
        const raw = (this.recipientSearch || '').trim().replace(/[;,]$/, '').trim()
        if (!raw) return
        // If it matches a directory user exactly, prefer that (captures their name); else treat as raw email.
        const match = this.recipientMatches[0]
        if (match && match.email && match.email.toLowerCase() === raw.toLowerCase()) {
          this.addRecipientUser(match)
          return
        }
        if (!this.basicEmailValid(raw)) {
          this.notesErrorMessage = `"${raw}" is not a valid email address.`
          return
        }
        if (!this.noteRecipients.some(r => (r.email || '').toLowerCase() === raw.toLowerCase())) {
          this.noteRecipients.push({ name: '', email: raw })
        }
        this.recipientSearch = ''
        this.showRecipientDropdown = false
        this.notesErrorMessage = ''
      },
      removeRecipient(idx) {
        this.noteRecipients.splice(idx, 1)
      },
      async sendNote() {
        if (!this.newNoteText || !this.noteRecipients.length) return
        const row = this.selectedNotesRow
        if (!row?.id) return
        this.noteSending = true
        this.notesErrorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.SendLockDeskQueueNote({
            item_id: row.id,
            note: this.newNoteText,
            submitted_by: this.currentUserName,
            submitted_by_azure_id: this.currentAzureId,
            recipients: this.noteRecipients.map(r => r.email).filter(Boolean)
          }, tokenResponse.accessToken)
          this.newNoteText = ''
          this.noteRecipients = []
          this.recipientSearch = ''
          row.has_notes = true
          await this.loadNotes()
        } catch (e) {
          this.notesErrorMessage = e?.response?.data?.message || e?.message || 'Failed to send note.'
        } finally {
          this.noteSending = false
        }
      },
      // Deletable by the note's author (matched on azure id) or a lock desk queue manager.
      canDeleteNote(note) {
        if (this.isLockDeskManager) return true
        const currentId = String(this.currentAzureId || '').toLowerCase()
        if (!currentId) return false
        const authorId = String(
          note?.submitted_by_azure_id || note?.created_by_azure_id || note?.created_by || ''
        ).toLowerCase()
        return !!authorId && authorId === currentId
      },
      promptDeleteNote(note) {
        this.pendingDeleteNoteId = note?.id || null
        this.notesErrorMessage = ''
      },
      cancelDeleteNote() {
        this.pendingDeleteNoteId = null
      },
      async confirmDeleteNote(note) {
        const row = this.selectedNotesRow
        if (!note?.id || !row?.id) return
        this.noteDeletingId = note.id
        this.notesErrorMessage = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          await api.DeleteLockDeskQueueNote({
            id: note.id,
            item_id: row.id,
            requested_by_azure_id: this.currentAzureId
          }, tokenResponse.accessToken)
          this.pendingDeleteNoteId = null
          await this.loadNotes()
          // Keep the grid's "has notes" indicator in sync when the last note is removed.
          row.has_notes = this.notes.length > 0
        } catch (e) {
          this.notesErrorMessage = e?.response?.data?.message || e?.message || 'Failed to delete note.'
        } finally {
          this.noteDeletingId = null
        }
      },
      formatTrackingDate(value) {
        const d = this._toDate(value)
        if (!d) return '—'
        return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      },
      formatDuration(seconds) {
        if (seconds == null) return '—'
        // A negative duration is impossible (end before start) — corrupt/out-of-order
        // timestamps upstream. Treat it as "no valid duration" rather than rendering a
        // nonsensical negative like "-249904s".
        if (seconds < 0) return '—'
        if (seconds < 60) return `${seconds}s`
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
        const h = Math.floor(m / 60)
        const rem = m % 60
        return rem > 0 ? `${h}h ${rem}m` : `${h}h`
      },
      openInsights() {
        this.insightsCopied = false
        this.showInsightsModal = true
        this.loadExtensionStats()
      },
      // 'all' means all time here, not the queue's 90-day fetch window — the panel shows the real
      // date range it covered so the scope is never ambiguous.
      async loadExtensionStats() {
        this.extStatsLoading = true
        this.extStatsError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const days = this.timeFrame === 'all' ? 3650 : this.getRequestDays()
          const response = await api.GetLockExtensionTypeStats(days, token)
          this.extStats = response?.data?.data ?? response?.data ?? null
        } catch (e) {
          this.extStats = null
          this.extStatsError = 'Could not load lock extension stats.'
        } finally {
          this.extStatsLoading = false
        }
      },
      closeInsights() {
        this.showInsightsModal = false
        this.aiPrompt = ''
        this.aiMessages = []
        this.aiError = ''
      },
      resetInsightFilters() {
        this.insightFilters = { request_type: '', status: '', channel: '', orgid: '', assignee: '' }
      },
      async copyInsights() {
        try {
          await navigator.clipboard.writeText(this.queueInsights.text)
          this.insightsCopied = true
          setTimeout(() => { this.insightsCopied = false }, 2000)
        } catch {
          // Clipboard unavailable — non-fatal
        }
      },
      formatAiResponse(text) {
        if (!text) return ''
        const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        return text.split('\n').map(line => {
          if (!line.trim()) return '<div class="ai-gap"></div>'
          // Sub-bullet (2+ leading spaces before -)
          if (/^ {2,}-\s/.test(line)) {
            return `<div class="ai-sub">${esc(line.trim().replace(/^-\s*/, ''))}</div>`
          }
          // Top-level bullet
          if (/^-\s/.test(line.trim())) {
            return `<div class="ai-bullet">${esc(line.trim().replace(/^-\s*/, ''))}</div>`
          }
          // Bold label pattern: "Word Word:" at start
          const headerMatch = line.trim().match(/^([A-Za-z][^:]{0,40}:)\s*(.*)$/)
          if (headerMatch) {
            const rest = headerMatch[2] ? ` <span class="ai-header-rest">${esc(headerMatch[2])}</span>` : ''
            return `<div class="ai-header"><span class="ai-header-label">${esc(headerMatch[1])}</span>${rest}</div>`
          }
          return `<div class="ai-line">${esc(line.trim())}</div>`
        }).join('')
      },
      async sendAiPrompt() {
        const question = this.aiPrompt.trim()
        if (!question || this.aiLoading) return
        this.aiMessages.push({ role: 'user', text: question })
        this.aiPrompt = ''
        this.aiLoading = true
        this.aiError = ''
        this.$nextTick(() => this._scrollAiToBottom())
        try {
          const rows = this.insightRows
          const rowSummary = rows.slice(0, 60).map(r =>
            `${r.loan_number || r.loan_id || '—'} | ${r.request_type || '—'} | ${r.status || '—'} | ${r.assigned_to_name || 'Unassigned'} | ${r.channel || '—'} | ${r.orgid || '—'}` +
            ` | turnaround:${this.formatDuration(r.duration_seconds)} | wait:${this.formatDuration(r.wait_seconds)} | pickup:${this.formatDuration(r.pickup_seconds)} | reassigns:${r.reassignment_count || 0}`
          ).join('\n')
          const briefText = this.queueInsights.lines.map(l => l.text).join('\n')
          const history = this.aiMessages.slice(-8).map(m =>
            `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`
          ).join('\n')
          const f = this.insightFilters
          const activeFilters = [
            f.request_type && `Type: ${this.requestTypeLabel(f.request_type)}`,
            f.status       && `Status: ${f.status}`,
            f.assignee     && `Assignee: ${(this.uniqueAssignees.find(u => u.id === f.assignee) || {}).name || f.assignee}`,
            f.channel      && `Channel: ${this.displayChannel(f.channel)}`,
            f.orgid        && `Org: ${f.orgid}`,
          ].filter(Boolean)
          const filterLine = activeFilters.length
            ? `Active filters — ${activeFilters.join(', ')}`
            : 'No filters active (showing all items in the selected time frame)'
          const systemContext = [
            `You are an AI assistant for a mortgage company's lock desk queue — the internal team that reviews and approves rate lock reprices and lock extensions before they go to the investor.`,
            ``,
            `DATA SCOPE:`,
            `- Time frame: ${this.timeFrameLabel}`,
            `- ${filterLine}`,
            `- All statistics and answers should be scoped to this filtered view (${rows.length} items).`,
            ``,
            `KEY TERMS:`,
            `- Request types (the queue supports dynamic types; treat any type in the data by its label below):`,
            this.requestTypeGlossary,
            `- Statuses: "not_started" = needs attention, "in_progress" = someone is working on it, "done" = resolved/completed.`,
            `- Assignee = the lock desk staff member handling the item.`,
            `- Channel = origination channel (e.g. Banked - Retail, Broker).`,
            `- Org = the branch/org ID the loan officer belongs to.`,
            `- Arrived = when the work item entered the queue (may differ from loan creation date).`,
            `- Timing metrics per item: "turnaround" = arrival → completion (total time in queue); "wait" = arrival → first pickup/assignment (how long before someone took it); "pickup" = assignment → completion (active handling time). '—' means not applicable yet (e.g. wait/turnaround blank until assigned/done).`,
            `- "reassigns" = how many times the item changed hands after the first pickup (0 = never reassigned).`,
            ``,
            `RESPONSE RULES:`,
            `- Never echo raw data rows back to the user. Interpret and summarize instead.`,
            `- Keep answers short and conversational unless the user asks for a detailed breakdown.`,
            `- Use bullet points only when listing 3+ items. Otherwise use a sentence.`,
            `- If counts or sums don't add up, note the discrepancy briefly rather than dwelling on it.`,
          ].join('\n')
          const fullPrompt = `${systemContext}\n\nCurrent queue snapshot (${rows.length} items in view):\n${briefText}\n\nFull item list (loan | type | status | assignee | channel | org | turnaround | wait | pickup | reassigns):\n${rowSummary}\n\nConversation so far:\n${history}\n\nRespond to the user's last message above.`
          const res = await api.GsaiPrompt(fullPrompt)
          this.aiMessages.push({ role: 'assistant', text: res.data || '' })
        } catch {
          this.aiError = 'Could not reach the AI service.'
        } finally {
          this.aiLoading = false
          this.$nextTick(() => this._scrollAiToBottom())
        }
      },
      _scrollAiToBottom() {
        const el = this.$el && this.$el.querySelector('.insights-ai-body')
        if (el) el.scrollTop = el.scrollHeight
      },
      _toDate(value) {
        if (!value) return null
        const str = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+') ? value + 'Z' : value
        const d = new Date(str)
        return Number.isNaN(d.getTime()) ? null : d
      },
      _countBy(rows, keyFn) {
        const counts = {}
        for (const r of rows) {
          const k = keyFn(r)
          if (!k) continue
          counts[k] = (counts[k] || 0) + 1
        }
        return counts
      },
      _topEntries(counts, n) {
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n)
      },
      _ago(ms) {
        const mins = Math.floor(ms / 60000)
        if (mins < 60) return `${mins}m`
        const h = Math.floor(mins / 60)
        if (h < 24) { const m = mins % 60; return m ? `${h}h ${m}m` : `${h}h` }
        const d = Math.floor(h / 24); const rh = h % 24
        return rh ? `${d}d ${rh}h` : `${d}d`
      },
      isOwnNote(note) {
        const currentId = String(this.currentAzureId || '').toLowerCase()
        if (!currentId) return false
        const createdBy = String(note?.created_by || note?.createdBy || '').toLowerCase()
        const createdByAzureId = String(note?.created_by_azure_id || note?.createdByAzureId || '').toLowerCase()
        return createdBy === currentId || createdByAzureId === currentId
      },

      // ── Concession approvers importer ────────────────────────────────────
      // The roster lives in the "Polly User Access Level" workbook. A manager
      // uploads it here; the server parses, diffs, and only writes on commit.
      openApproverImport() {
        this.showApproverModal = true
        this.resetApproverImport()
        this.loadApproverRosterSummary()
        this.loadConcessionReasonsSummary()
      },
      closeApproverImport() {
        if (this.approverBusy) return
        this.showApproverModal = false
        this.resetApproverImport()
      },
      resetApproverImport() {
        this.approverFile = null
        this.approverPreview = null
        this.approverError = ''
        this.approverDragOver = false
        this.approverCommitting = false
        this.showApproverWarnings = false
        this.showApproverChanges = false
        if (this.$refs.approverFileInput) this.$refs.approverFileInput.value = ''
      },
      async loadApproverRosterSummary() {
        this.approverRosterLoading = true
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const [rosterRes, importRes] = await Promise.allSettled([
            api.GetConcessionApprovers(true, token),
            api.GetConcessionApproverImports(1, token)
          ])

          if (rosterRes.status === 'fulfilled') {
            const data = rosterRes.value?.data?.data || rosterRes.value?.data || []
            this.approverRosterCount = Array.isArray(data) ? data.length : 0
          }

          if (importRes.status === 'fulfilled') {
            const data = importRes.value?.data?.data || importRes.value?.data || []
            this.approverLastImport = Array.isArray(data) && data.length ? data[0] : null
          }
        } catch {
          // A failed summary is cosmetic — the upload itself still works.
        } finally {
          this.approverRosterLoading = false
        }
      },
      onApproverFilePicked(event) {
        const file = event?.target?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      onApproverDrop(event) {
        this.approverDragOver = false
        const file = event?.dataTransfer?.files?.[0]
        if (file) this.setApproverFile(file)
      },
      setApproverFile(file) {
        const name = (file.name || '').toLowerCase()
        if (!name.endsWith('.xlsx') && !name.endsWith('.xlsm')) {
          this.approverError = 'That is not an Excel workbook. Pick the .xlsx export from Polly.'
          return
        }

        this.approverFile = file
        // A new file invalidates any preview on screen — otherwise a manager
        // could confirm one file's numbers and apply a different file.
        this.approverPreview = null
        this.approverError = ''
        this.showApproverWarnings = false
        this.showApproverChanges = false
      },
      previewApproverImport() {
        return this._runApproverImport(false)
      },
      commitApproverImport() {
        return this._runApproverImport(true)
      },
      async _runApproverImport(commit) {
        if (!this.approverFile || this.approverBusy) return

        this.approverBusy = true
        this.approverCommitting = commit
        this.approverError = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken
          const res = await api.ImportConcessionApprovers(this.approverFile, commit, token)

          const body = res?.data || {}
          const report = body.data || body

          // The API lowercases every PascalCase property (LowerCaseNamingPolicy
          // in Startup.cs), so the ServiceResponse flag arrives as `issuccess`.
          // Our own DTO fields carry [JsonPropertyName] and keep snake_case.
          const succeeded = body.issuccess !== undefined ? body.issuccess : body.isSuccess

          if (succeeded === false) {
            this.approverError = body.message || 'The import failed.'
            // Keep whatever the server managed to report, so counts and
            // warnings are still visible alongside the error.
            if (report && typeof report === 'object') this.approverPreview = report
            return
          }

          this.approverPreview = report

          if (commit) {
            // Refresh the summary line so the roster count reflects the import.
            this.loadApproverRosterSummary()
          } else if (report?.warnings_total > 0) {
            this.showApproverWarnings = true
          }
        } catch (e) {
          // A parse failure or a refused empty file comes back as a 400, so the
          // useful text is on the error response, not the success path.
          const serverMessage = e?.response?.data?.message
            || e?.response?.data?.error
          this.approverError = serverMessage
            || 'Could not reach the server to run the import. Nothing was changed.'
        } finally {
          this.approverBusy = false
          this.approverCommitting = false
        }
      },
      async loadConcessionReasonsSummary() {
        this.reasonsLoading = true
        this.reasonSyncError = ''
        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.GetConcessionReasons(null, tokenResponse.accessToken)
          const data = res?.data?.data || []
          this.applyReasonSummary(Array.isArray(data) ? data : [])
        } catch {
          // Cosmetic — the sync button still works if this read failed.
        } finally {
          this.reasonsLoading = false
        }
      },
      applyReasonSummary(reasons) {
        this.reasonCount = reasons.length
        // Newest sync stamp across the list. Nulls are omitted by the API, so
        // filter before comparing.
        const stamps = reasons
          .map(r => r.polly_synced_utc)
          .filter(Boolean)
          .sort()
        this.reasonLastSynced = stamps.length ? stamps[stamps.length - 1] : null
      },
      async syncConcessionReasons() {
        if (this.reasonSyncing) return

        this.reasonSyncing = true
        this.reasonSyncError = ''
        this.reasonSyncMessage = ''

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const res = await api.SyncConcessionReasonsFromPolly(tokenResponse.accessToken)
          const body = res?.data || {}
          const result = body.data || {}

          this.reasonSyncMessage = body.message || 'Reasons synced.'
          this.reasonUnmappedTypes = Array.isArray(result.unmapped_types) ? result.unmapped_types : []
          this.applyReasonSummary(Array.isArray(result.reasons) ? result.reasons : [])
        } catch (e) {
          this.reasonSyncError = e?.response?.data?.message
            || 'Could not reach Polly to refresh the reason list. The existing reasons are unchanged.'
        } finally {
          this.reasonSyncing = false
        }
      },
      changeTypeLabel(changeType) {
        switch (changeType) {
          case 'new': return 'New'
          case 'changed': return 'Updated'
          case 'deactivating': return 'Deactivating'
          case 'reactivating': return 'Reactivating'
          default: return changeType || ''
        }
      },
      formatFileSize(bytes) {
        const size = Number(bytes)
        if (!size || Number.isNaN(size)) return ''
        if (size < 1024) return `${size} B`
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`
        return `${(size / (1024 * 1024)).toFixed(1)} MB`
      }
    }
  }
</script>

<style scoped>
  /* Sized as the leftover of #app's 100vh flex column (App.vue: header, then the routed page)
     instead of min-height: calc(100vh - 90px). The header is ~100px, not 90, and it moves with
     the rem breakpoints in cgslight.css, so any hardcoded subtraction overshoots the viewport
     and #app — overflow-y: auto, courtesy of the overflow-x: hidden in mobile.css — grows a
     full-height scrollbar around the whole app. Scrolling belongs to .table-wrap.
     overflow is auto rather than hidden so a very short window scrolls instead of clipping
     the pagination bar. */
  .lock-desk-queue-page {
    padding: 18px;
    background: #f7f8fb;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    color: #1f2a44;
  }

  /* Everything above the table keeps its natural height — only .table-card gives ground. */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
    flex: 0 0 auto;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex: 0 0 auto;
  }

  .page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  /* ── Summary ─────────────────────────────────────────────────────────────── */
  .summary-sections {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
    flex: 0 0 auto;
  }

  .summary-section {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    padding: 10px;
  }

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }

  .section-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .section-cards {
    display: grid;
    gap: 8px;
  }

  .overview-cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  /* auto-fill (not auto-fit) so cards keep their compact width instead of stretching to fill the
     row — with only two or three types the old auto-fit made each one enormous. */
  .type-cards {
    grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  }

  .summary-card {
    background: #fdfdfe;
    border: 1px solid #e8ebf2;
    border-radius: 5px;
    padding: 8px 10px;
    min-width: 0;
  }

  .type-cards-empty {
    font-size: 12px;
    color: #a8afc0;
    font-style: italic;
    padding: 4px 2px;
  }

  .not-started-card { border-color: #e2e8f0; }
  .in-progress-card { border-color: #fde7a6; background: #fffdf4; }
  .done-card        { border-color: #c3e8d4; background: #f6fdf8; }

  .summary-card-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  .summary-icon {
    font-size: 11px;
    color: #6a7b9f;
    flex-shrink: 0;
  }

  .summary-label {
    font-size: 11px;
    color: #7b8397;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .summary-value {
    font-size: 17px;
    font-weight: 800;
    color: #1f2a44;
    line-height: 1.1;
  }

  /* ── Toolbar ─────────────────────────────────────────────────────────────── */
  .toolbar {
    margin-bottom: 10px;
    flex: 0 0 auto;
  }

  .single-line-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) 160px 170px 170px 170px;
    gap: 8px;
    align-items: center;
  }

  .search-input,
  .filter-select {
    height: 38px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    padding: 0 14px;
    font-size: 13px;
    width: 100%;
    color: #23314f;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .search-input::placeholder { color: #94a0b8; }

  .search-input:focus,
  .filter-select:focus,
  .notes-textarea:focus {
    border-color: #cfd8e8;
    box-shadow: 0 0 0 2px rgba(49, 93, 181, 0.08);
  }

  .days-select { min-width: 140px; }

  /* ── Table Card ──────────────────────────────────────────────────────────── */
  /* Absorbs whatever height is left, down to a floor that still shows a few rows; past that
     the page scrolls rather than squeezing the table into a sliver. */
  .table-card {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    overflow: hidden;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 220px;
  }

  .table-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid #e9edf3;
    background: #fafbfd;
  }

  .table-card-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .table-card-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .table-card-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .table-card-count {
    font-size: 13px;
    font-weight: 700;
    color: #7b8397;
  }

  .table-wrap {
    overflow: auto;
    flex: 1;
  }

  .table-state {
    padding: 32px 20px;
    text-align: center;
    font-size: 14px;
    color: #7b8397;
  }

  .table-state.error { color: #c0392b; }

  /* ── Table ───────────────────────────────────────────────────────────────── */
  .locks-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }

  .locks-table thead th {
    background: #f5f7fb;
    border-bottom: 1px solid #e5eaf2;
    padding: 9px 12px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #4a5878;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
  }

  .locks-table thead th:hover { background: #eef1f8; }

  .locks-table tbody tr {
    border-bottom: 1px solid #eef0f5;
    transition: background 0.1s ease;
  }

  .locks-table tbody tr:hover { background: #f7f9fd; }

  .locks-table tbody tr.done-row { opacity: 0.6; }

  .locks-table td {
    padding: 9px 12px;
    color: #2b3a57;
    vertical-align: middle;
  }

  .compact-cell { white-space: nowrap; }

  .primary-text {
    font-weight: 600;
    color: #1f2a44;
  }

  /* ── Request Type Pill ───────────────────────────────────────────────────── */
  .type-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .type-pill-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .type-pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }

  .dup-flag {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
    white-space: nowrap;
    background: #fff4e5;
    color: #9a5b00;
    border: 1px solid #f3d29a;
    cursor: help;
  }
  .dup-flag .fa-duotone {
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 0.4;
  }

  .type-extension { background: #eef4ff; color: #2a55b3; border: 1px solid #cfdcf8; }
  .type-reprice   { background: #f0fbf5; color: #1b7a47; border: 1px solid #b8e5cc; }
  .type-float     { background: #fdf4e7; color: #8a5a14; border: 1px solid #f3dfb7; }
  .type-revenue   { background: #fdecec; color: #a4302a; border: 1px solid #f4c4c1; }
  .type-lock-request { background: #e3f2fd; color: #1565a6; border: 1px solid #b6ddf5; }
  .type-other     { background: #f3f4f8; color: #5a6680; border: 1px solid #dde2eb; }

  .request-desc {
    font-size: 12.5px;
    color: #4a5268;
    line-height: 1.35;
    max-width: 300px;
    /* Show the full description — wrap to as many lines as needed, never truncate. */
    white-space: normal;
    overflow-wrap: anywhere;
  }

  /* ── Status Pill Button ──────────────────────────────────────────────────── */
  .status-pill-btn {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    border: 1px solid transparent;
    cursor: pointer;
    transition: opacity 0.15s ease;
    white-space: nowrap;
  }

  .status-pill-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .status-not-started {
    background: #f3f4f8;
    color: #5a6680;
    border-color: #dde2eb;
  }

  .status-in-progress {
    background: #fef9ec;
    color: #8a5a14;
    border-color: #f6dfa0;
  }

  .status-done {
    background: #eef9f0;
    color: #2c7a4b;
    border-color: #c3e8d4;
  }

  /* ── Assigned Cell ───────────────────────────────────────────────────────── */
  .assigned-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .assigned-name {
    font-size: 13px;
    color: #2b3a57;
  }

  .claim-btn {
    height: 26px;
    padding: 0 9px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid #d9e2f0;
    border-radius: 4px;
    background: #f5f8fc;
    color: #315db5;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
  }

  .claim-btn:hover { background: #eaf0fc; }
  .claim-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .claim-btn.is-mine {
    background: #eef9f0;
    color: #2c7a4b;
    border-color: #c3e8d4;
  }

  .assign-to-btn {
    height: 26px;
    width: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    border: 1px solid #d9e2f0;
    border-radius: 4px;
    background: #f5f8fc;
    color: #6b4bb5;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .assign-to-btn:hover { background: #f0ecfb; }
  .assign-to-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  /* ── Assign Modal ────────────────────────────────────────────────────────── */
  .assign-modal-card { max-width: 460px; }
  .assign-modal-body { display: flex; flex-direction: column; gap: 12px; }

  .assign-current {
    font-size: 12.5px;
    color: #5a6b8c;
    background: #f5f8fc;
    border: 1px solid #e3ebf5;
    border-radius: 6px;
    padding: 8px 10px;
  }
  .assign-current strong { color: #2b3a57; }

  .assign-search-wrap { position: relative; }
  .assign-search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #9aa8c2;
    font-size: 13px;
  }
  .assign-search-input {
    width: 100%;
    height: 38px;
    padding: 0 12px 0 34px;
    border: 1px solid #d9e2f0;
    border-radius: 6px;
    font-size: 14px;
    color: #2b3a57;
    box-sizing: border-box;
  }
  .assign-search-input:focus { outline: none; border-color: #8f7bd6; }

  .assign-list {
    max-height: 300px;
    overflow-y: auto;
    border: 1px solid #eef2f8;
    border-radius: 8px;
  }
  .assign-list-state {
    padding: 16px;
    text-align: center;
    color: #8a97b0;
    font-size: 13px;
  }

  .assign-user-row {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    background: transparent;
    border: none;
    border-bottom: 1px solid #f1f5fa;
    cursor: pointer;
    text-align: left;
    transition: background 0.12s ease;
  }
  .assign-user-row:last-child { border-bottom: none; }
  .assign-user-row:hover { background: #f6f4fd; }
  .assign-user-row:disabled { opacity: 0.6; cursor: not-allowed; }
  .assign-user-row.is-current { background: #eef9f0; }

  .assign-user-avatar {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #6b4bb5;
    background: #ece7f9;
  }
  .assign-user-info { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
  .assign-user-name { font-size: 13.5px; font-weight: 600; color: #2b3a57; }
  .assign-user-email { font-size: 11.5px; color: #8a97b0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .assign-user-tag {
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 2px 7px;
    border-radius: 10px;
    background: #eef2f8;
    color: #7688a8;
  }
  .assign-user-tag.current-tag { background: #d9f0e2; color: #2c7a4b; }

  .assign-add-section { border-top: 1px dashed #e3ebf5; padding-top: 10px; }
  .assign-add-toggle {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12.5px;
    font-weight: 600;
    color: #6b4bb5;
    background: transparent;
    border: 1px dashed #cdbef0;
    border-radius: 6px;
    padding: 8px 12px;
    width: 100%;
    justify-content: center;
    cursor: pointer;
  }
  .assign-add-toggle:hover { background: #f6f4fd; }

  .assign-add-form { display: flex; flex-direction: column; gap: 8px; }
  .assign-add-title { font-size: 12.5px; font-weight: 700; color: #2b3a57; }
  .assign-add-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .assign-add-hint { font-size: 11.5px; color: #8a97b0; }
  .assign-add-actions { display: flex; justify-content: flex-end; gap: 8px; }

  .assign-unassign-btn {
    margin-right: auto;
    height: 34px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid #f0d4d4;
    border-radius: 6px;
    background: #fdf4f4;
    color: #b5423a;
    cursor: pointer;
  }
  .assign-unassign-btn:hover { background: #fbeaea; }
  .assign-unassign-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  /* ── Notes Button ────────────────────────────────────────────────────────── */
  .notes-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 10px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 5px;
    border: 1px solid #d9dfeb;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
  }

  .notes-btn.has-notes { color: #315db5; background: #eef4ff; border-color: #d7e5fd; }
  .notes-btn.no-notes  { color: #8a5a14; background: #fff7e8; border-color: #f3dfb7; }
  .notes-btn:disabled  { opacity: 0.55; cursor: not-allowed; }

  /* ── Link Actions ────────────────────────────────────────────────────────── */
  .link-actions {
    display: flex;
    gap: 6px;
    flex-wrap: nowrap;
  }

  /* Shared shape for the row's outbound loan links; colour is per-button below. */
  .polly-btn,
  .encompass-btn {
    display: inline-block;
    height: 26px;
    line-height: 24px;
    padding: 0 11px;
    font-size: 11px;
    font-weight: 700;
    border-radius: 4px;
    white-space: nowrap;
    cursor: pointer;
    transition: opacity 0.15s ease;
  }
  .polly-btn:hover:not(:disabled),
  .encompass-btn:hover:not(:disabled) { opacity: 0.8; }
  .polly-btn:disabled,
  .encompass-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .polly-btn {
    background: #f0fbf5;
    color: #1b7a47;
    border: 1px solid #b8e5cc;
  }

  .encompass-btn {
    background: #eef4ff;
    color: #123a71;
    border: 1px solid #c2d4f0;
  }

  /* ── Pagination ──────────────────────────────────────────────────────────── */
  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
    padding: 8px 2px;
    flex: 0 0 auto;
  }

  .pagination-info {
    font-size: 13px;
    color: #7b8397;
  }

  .pagination-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-number {
    font-size: 13px;
    color: #4a5878;
    font-weight: 600;
  }

  .page-btn,
  .refresh-btn {
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #22314f;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .page-btn:hover,
  .refresh-btn:hover { background: #f4f7fc; border-color: #cfd8e8; }

  .page-btn:disabled,
  .refresh-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .add-btn {
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    border: 1px solid #123a71;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s ease;
  }
  .add-btn:hover { background: #0d2d58; border-color: #0d2d58; }

  .insights-btn {
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .insights-btn:hover { background: #eef4ff; border-color: #c2d4f0; color: #123a71; }
  .insights-btn .fa-duotone,
  .insights-btn .fa-duotone::before,
  .insights-btn .fa-duotone::after {
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
  }

  .insights-modal-card { max-width: 1380px; width: 98vw; }
  .insights-modal-body {
    display: flex;
    flex-direction: column;
    padding: 14px 18px 0;
    overflow: hidden;
    flex: 1;
    min-height: 0;
  }

  .insights-split {
    display: flex;
    gap: 14px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    margin-bottom: 0;
  }

  /* Left scrollable column */
  .insights-left {
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    padding-right: 6px;
    padding-bottom: 14px;
  }

  .insights-filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-bottom: 12px;
  }
  .insights-filter-select {
    height: 32px;
    min-width: 120px;
    flex: 0 1 auto;
    font-size: 12.5px;
  }
  .insights-clear-btn {
    height: 32px;
    padding: 0 10px;
    border: 1px solid #e0c4c4;
    border-radius: 5px;
    background: #fff5f5;
    color: #b3261e;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .insights-clear-btn:hover { background: #fdebeb; border-color: #d99; }

  .insights-empty {
    padding: 28px 0;
    text-align: center;
    color: #7b8397;
    font-size: 13.5px;
  }

  .insights-charts {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }
  .insight-chart-card {
    border: 1px solid #e6ebf4;
    border-radius: 8px;
    padding: 10px 8px 6px;
    background: #fbfcfe;
    height: 220px;
    overflow: visible;
  }
  .insight-chart-card-wide {
    grid-column: 1 / -1;
    height: auto;
    overflow: visible;
    padding: 10px 12px 12px;
  }
  .insight-chart-title {
    font-size: 12px;
    font-weight: 600;
    color: #44506a;
    margin-bottom: 4px;
    padding-left: 4px;
  }

  /* Assignee CSS bar chart */
  .assignee-bars { padding: 2px 0 0; }
  .assignee-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 7px;
  }
  .assignee-row-name {
    width: 120px;
    flex-shrink: 0;
    font-size: 11.5px;
    color: #44506a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .assignee-row-track {
    flex: 1;
    display: flex;
    height: 14px;
    border-radius: 3px;
    overflow: hidden;
    background: #eef1f7;
    min-width: 0;
  }
  .assignee-bar-done {
    background: #3fa66a;
    transition: width 0.3s ease;
    min-width: 0;
  }
  .assignee-bar-open {
    background: #f0b429;
    transition: width 0.3s ease;
    min-width: 0;
  }
  .assignee-row-count {
    width: 22px;
    text-align: right;
    font-size: 11.5px;
    color: #44506a;
    flex-shrink: 0;
  }
  .assignee-legend {
    display: flex;
    gap: 14px;
    margin-top: 8px;
    font-size: 11.5px;
    color: #5a6680;
    padding-left: 128px;
  }
  .assignee-swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 4px;
    vertical-align: middle;
  }
  .assignee-swatch-done { background: #3fa66a; }
  .assignee-swatch-open { background: #f0b429; }

  /* Timing card */
  .timing-stages {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
  }
  .timing-stage {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 10px 6px;
    background: #f4f7fc;
    border: 1px solid #e6ebf4;
    border-radius: 8px;
  }
  .timing-stage-value {
    font-size: 16px;
    font-weight: 700;
    color: #2a4a8c;
  }
  .timing-stage-label {
    font-size: 11px;
    color: #6b7790;
    text-align: center;
    line-height: 1.25;
  }
  .insight-chart-subtitle {
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7790;
    margin-bottom: 8px;
  }
  .timing-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 7px;
  }
  .timing-row-name {
    width: 120px;
    flex-shrink: 0;
    font-size: 11.5px;
    color: #44506a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .timing-track {
    flex: 1;
    height: 14px;
    border-radius: 3px;
    overflow: hidden;
    background: #eef1f7;
    min-width: 0;
  }
  .timing-bar {
    height: 100%;
    background: #6a8fd8;
    transition: width 0.3s ease;
    min-width: 2px;
  }
  .timing-row-value {
    width: 56px;
    text-align: right;
    font-size: 11.5px;
    color: #44506a;
    flex-shrink: 0;
  }
  .timing-empty {
    font-size: 12.5px;
    color: #8a96b0;
    padding: 6px 0 2px;
  }

  /* Lock extension splits — two stacked proportion bars, one per question */
  .ext-scope {
    padding-left: 4px;
    margin-bottom: 8px;
  }
  .ext-split {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 14px 20px;
    padding: 2px 4px 0;
  }
  .ext-block { min-width: 0; }
  .ext-block-title {
    font-size: 12.5px;
    font-weight: 600;
    color: #2b3650;
  }
  .ext-block-sub {
    font-size: 11.5px;
    color: #8a96b0;
    margin-top: 2px;
  }
  .ext-bar {
    display: flex;
    gap: 2px;
    height: 22px;
    margin: 10px 0;
  }
  .ext-bar-seg {
    border-radius: 4px;
    min-width: 3px;
  }
  .ext-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 22px;
  }
  .ext-legend-item {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .ext-legend-head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #44506a;
  }
  .ext-swatch {
    width: 9px;
    height: 9px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .ext-swatch-muted { background: #b4b2a9; }
  .ext-legend-val {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding-left: 15px;
    font-size: 18px;
    font-weight: 600;
    color: #1f2a44;
  }
  .ext-legend-count {
    font-size: 11.5px;
    font-weight: 400;
    color: #8a96b0;
  }
  .ext-legend-detail {
    padding-left: 15px;
    font-size: 11px;
    color: #8a96b0;
  }
  /* Sits inside the type block, so no border-top — a rule here would stop mid-row */
  .ext-note {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 11px;
    font-size: 11.5px;
    line-height: 1.45;
    color: #6b7794;
  }
  .ext-note .ext-swatch { margin-top: 4px; }
  .ext-why {
    margin: 14px 4px 0;
    padding: 9px 11px;
    background: #f7f9fc;
    border: 1px solid #e6ebf4;
    border-radius: 8px;
    font-size: 11.5px;
    line-height: 1.5;
    color: #6b7794;
  }
  .ext-why-label {
    font-weight: 600;
    color: #2b3650;
  }
  .ext-why code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    color: #44506a;
  }

  .insights-list {
    list-style: none;
    margin: 0 0 0 0;
    padding: 10px 14px;
    background: #f7f9fc;
    border: 1px solid #e6ebf4;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 9px;
  }
  .insight-line {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: #2b3650;
  }
  .insight-line .fa-duotone {
    color: #2f6bd8;
    margin-top: 2px;
    flex-shrink: 0;
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 0.4;
  }

  /* Right AI panel */
  .insights-ai-panel {
    width: 400px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #d4e0f7;
    border-radius: 8px;
    background: #f3f6ff;
    padding: 14px 14px 12px;
    overflow: hidden;
  }
  .insights-ai-label {
    font-size: 13px;
    font-weight: 600;
    color: #2f6bd8;
    margin-bottom: 10px;
    flex-shrink: 0;
  }
  .insights-ai-label .fa-duotone {
    margin-right: 6px;
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 0.4;
  }
  .insights-ai-body {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 10px;
    min-height: 60px;
  }
  .insights-ai-placeholder {
    font-size: 12.5px;
    color: #8a96b0;
    line-height: 1.55;
    padding: 4px 0;
  }
  .insights-ai-thinking {
    font-size: 13px;
    color: #2f6bd8;
    padding: 4px 0;
  }
  .insights-ai-input-row {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }
  .insights-ai-input {
    flex: 1;
    padding: 7px 10px;
    font-size: 13px;
    border: 1px solid #c5d6f0;
    border-radius: 6px;
    background: #fff;
    color: #1e2a40;
    outline: none;
    min-width: 0;
  }
  .insights-ai-input:focus {
    border-color: #2f6bd8;
    box-shadow: 0 0 0 2px rgba(47,107,216,0.15);
  }
  .insights-ai-input:disabled { background: #eef1f7; color: #8a96b0; }
  .insights-ai-btn {
    white-space: nowrap;
    padding: 7px 14px;
    font-size: 13px;
    border: none;
    border-radius: 6px;
    background: #2f6bd8;
    color: #fff;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.15s;
    flex-shrink: 0;
  }
  .insights-ai-btn:hover:not(:disabled) { background: #1a57c2; }
  .insights-ai-btn:disabled { background: #a0b8e0; cursor: not-allowed; }
  .insights-ai-formatted { font-size: 12.5px; line-height: 1.5; color: #1e2a40; }
  .insights-ai-formatted .ai-line { margin-bottom: 5px; }
  .insights-ai-formatted .ai-gap { height: 7px; }
  .insights-ai-formatted .ai-header {
    margin: 8px 0 3px;
    font-size: 12px;
  }
  .insights-ai-formatted .ai-header-label {
    font-weight: 700;
    color: #2a4a8c;
  }
  .insights-ai-formatted .ai-header-rest { color: #1e2a40; }
  .insights-ai-formatted .ai-bullet {
    display: flex;
    gap: 6px;
    margin-bottom: 3px;
    padding-left: 2px;
  }
  .insights-ai-formatted .ai-bullet::before {
    content: '•';
    color: #2f6bd8;
    flex-shrink: 0;
    font-size: 13px;
    line-height: 1.4;
  }
  .insights-ai-formatted .ai-sub {
    display: flex;
    gap: 6px;
    margin-bottom: 2px;
    padding-left: 14px;
    font-size: 12px;
    color: #44506a;
  }
  .insights-ai-formatted .ai-sub::before {
    content: '–';
    color: #8a96b0;
    flex-shrink: 0;
    line-height: 1.4;
  }
  .insights-ai-error {
    font-size: 12px;
    color: #c0392b;
    line-height: 1.4;
    padding: 4px 2px;
  }

  /* Chat bubbles */
  .ai-bubble {
    max-width: 92%;
    padding: 8px 11px;
    border-radius: 12px;
    font-size: 12.5px;
    line-height: 1.5;
    margin-bottom: 8px;
    word-break: break-word;
  }
  .ai-bubble-user {
    background: #2f6bd8;
    color: #fff;
    align-self: flex-end;
    margin-left: auto;
    border-bottom-right-radius: 4px;
  }
  .ai-bubble-assistant {
    background: #fff;
    border: 1px solid #dce6f5;
    color: #1e2a40;
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  }
  .ai-bubble-typing {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 14px;
    min-width: 52px;
  }
  .ai-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #8aabdf;
    animation: ai-bounce 1.2s infinite ease-in-out;
  }
  .ai-dot:nth-child(2) { animation-delay: 0.2s; }
  .ai-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes ai-bounce {
    0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }

  /* Make the AI body a flex column so bubbles stack */
  .insights-ai-body {
    display: flex;
    flex-direction: column;
  }

  .tracking-cell {
    font-size: 12px;
    color: #5a6680;
    white-space: nowrap;
    background: #fffdf4;
    border-left: 1px dashed #f0e6c0;
  }

  .credited-pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    background: #eef9f0;
    color: #2c7a4b;
    border: 1px solid #c3e8d4;
  }

  .credited-pill.credited-diff {
    background: #fff8e6;
    color: #8a5a00;
    border-color: #f0d890;
  }

  .reassign-count-btn {
    border: 1px solid #f0d890;
    background: #fff8e6;
    color: #8a5a00;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .reassign-count-btn:hover { background: #fdeecb; }

  .assignment-timeline {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .assignment-event {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .assignment-badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
    margin-top: 1px;
  }
  .assignment-badge.badge-pickup { background: #eef9f0; color: #2c7a4b; border: 1px solid #c3e8d4; }
  .assignment-badge.badge-reassign { background: #fff8e6; color: #8a5a00; border: 1px solid #f0d890; }
  .assignment-to { font-size: 13.5px; font-weight: 600; color: #2b3650; }
  .assignment-meta { font-size: 12px; color: #7b8397; margin-top: 1px; }
  .add-btn .fa-duotone,
  .add-btn .fa-duotone::before,
  .add-btn .fa-duotone::after {
    --fa-primary-color: #ffffff;
    --fa-secondary-color: #ffffff;
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
  }

  /* ── Modals ──────────────────────────────────────────────────────────────── */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
  }

  .modal-card {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
  }

  .status-modal-card  { max-width: 420px; }
  .notes-modal-card   { max-width: 560px; }
  .create-modal-card  { max-width: 660px; }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 18px 14px;
    border-bottom: 1px solid #eef0f5;
  }

  .modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #1f2a44;
  }

  .modal-subtitle {
    margin: 3px 0 0;
    font-size: 13px;
    color: #7b8397;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #7b8397;
    font-size: 15px;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s ease;
  }

  .close-btn:hover { background: #f0f2f7; color: #1f2a44; }

  .modal-body {
    padding: 18px;
    overflow-y: auto;
    flex: 1;
  }

  /* Status Options */
  .status-options {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }

  .status-option-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 14px 10px;
    border: 2px solid #dde2eb;
    border-radius: 6px;
    background: #fafbfd;
    color: #5a6680;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .status-option-btn i { font-size: 18px; }

  .status-option-btn:hover { background: #f0f2f8; border-color: #c5cde0; }

  .status-option-btn.active {
    border-color: #315db5;
    background: #eef4ff;
    color: #1f3f8a;
  }

  .status-option-btn.in-progress-option.active {
    border-color: #d4a017;
    background: #fef9ec;
    color: #7a4e0a;
  }

  .status-option-btn.done-option.active {
    border-color: #2c7a4b;
    background: #eef9f0;
    color: #1e5c35;
  }

  .inline-error {
    font-size: 13px;
    color: #c0392b;
    margin-bottom: 12px;
  }

  .modal-footer-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .save-btn {
    height: 36px;
    padding: 0 18px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    border: 1px solid #0b84ff;
    background: #0b84ff;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .save-btn:hover  { background: #0677e6; border-color: #0677e6; }
  .save-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .cancel-btn {
    height: 36px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    border: 1px solid #dde2eb;
    background: #f5f6f8;
    color: #6b7486;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .cancel-btn:hover { background: #ebedf2; }

  /* Create Modal */
  .create-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px 16px;
    margin-bottom: 16px;
  }

  .form-group { display: flex; flex-direction: column; gap: 5px; }
  .form-group-full { grid-column: 1 / -1; }

  .form-label {
    font-size: 12px;
    font-weight: 600;
    color: #4a5468;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .required-star { color: #e5534b; margin-left: 2px; }

  .form-input,
  .form-select {
    height: 36px;
    padding: 0 10px;
    font-size: 13px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #1f2a44;
    outline: none;
    transition: border-color 0.15s ease;
  }

  .form-input:focus,
  .form-select:focus { border-color: #0b84ff; box-shadow: 0 0 0 2px rgba(11,132,255,0.12); }

  .form-textarea {
    padding: 8px 10px;
    font-size: 13px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #1f2a44;
    outline: none;
    resize: vertical;
    font-family: inherit;
    transition: border-color 0.15s ease;
  }

  .form-textarea:focus { border-color: #0b84ff; box-shadow: 0 0 0 2px rgba(11,132,255,0.12); }

  .error-banner {
    padding: 9px 12px;
    background: #fff0ef;
    border: 1px solid #f7c5c2;
    border-radius: 5px;
    color: #b0332b;
    font-size: 13px;
    margin-bottom: 14px;
  }

  .modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
  }

  /* Notes Modal */
  .notes-modal-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 200px;
  }

  .notes-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 2px;
  }

  .note-item {
    background: #f8f9fc;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    padding: 10px 12px;
  }

  .note-item.own-note {
    background: #eef4ff;
    border-color: #d7e5fd;
  }

  .note-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
  }

  .note-author {
    font-size: 12px;
    font-weight: 700;
    color: #3a4a6a;
  }

  .note-date {
    font-size: 11px;
    color: #9aa0b2;
    margin-left: auto;
  }

  .note-scope-badge {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    border-radius: 4px;
    padding: 1px 6px;
    line-height: 1.6;
  }

  .note-scope-badge.scope-external {
    color: #0b6b52;
    background: #dff5ec;
    border: 1px solid #b6e6d5;
  }

  .note-scope-badge.scope-internal {
    color: #5b6472;
    background: #eef1f6;
    border: 1px solid #dde3ee;
  }

  .note-delete-btn {
    border: none;
    background: transparent;
    color: #a2abbd;
    cursor: pointer;
    font-size: 13px;
    padding: 2px 4px;
    line-height: 1;
    border-radius: 4px;
  }

  .note-delete-btn:hover { color: #d0342c; background: #fbeceb; }

  .note-delete-confirm {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    padding: 7px 10px;
    background: #fbeceb;
    border: 1px solid #f3c7c3;
    border-radius: 6px;
  }

  .note-delete-text { font-size: 12px; color: #8a2a24; font-weight: 600; flex: 1; }

  .note-delete-yes {
    border: none;
    background: #d0342c;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 5px;
    cursor: pointer;
  }

  .note-delete-yes:disabled { opacity: 0.6; cursor: default; }

  .note-delete-no {
    border: 1px solid #dfe4ee;
    background: #fff;
    color: #5b6472;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 5px;
    cursor: pointer;
  }

  .note-body {
    font-size: 13px;
    color: #2b3a57;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .notes-composer { border-top: 1px solid #e7ebf2; padding-top: 14px; }

  .notes-textarea {
    width: 100%;
    border: 1px solid #dfe4ee;
    border-radius: 5px;
    padding: 10px 12px;
    font-size: 13px;
    color: #23314f;
    resize: vertical;
    font-family: inherit;
    background: #fcfcfe;
    outline: none;
    box-sizing: border-box;
  }

  .notes-textarea:disabled { opacity: 0.7; }

  .notes-composer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
  }

  .notes-compose-hint {
    font-size: 12px;
    color: #94a0b8;
  }

  /* ── Internal/External compose scope ─────────────────────────────────────── */
  .compose-scope-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .compose-scope-label {
    font-size: 12px;
    font-weight: 700;
    color: #3a4a6a;
  }

  .scope-toggle {
    display: inline-flex;
    border: 1px solid #dfe4ee;
    border-radius: 6px;
    overflow: hidden;
  }

  .scope-toggle-btn {
    border: none;
    background: #fcfcfe;
    color: #5b6472;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    cursor: pointer;
    outline: none;
  }

  .scope-toggle-btn + .scope-toggle-btn { border-left: 1px solid #dfe4ee; }
  .scope-toggle-btn:disabled { cursor: default; opacity: 0.6; }

  .scope-toggle-btn.active {
    background: #5b6472;
    color: #fff;
  }

  .scope-toggle-btn.active-external {
    background: #0b8a63;
    color: #fff;
  }

  .compose-scope-hint {
    font-size: 12px;
    font-weight: 600;
    color: #7a8399;
  }

  /* A visible left accent (+ tinted textarea for external) makes the active scope obvious */
  .notes-composer.composer-internal { border-left: 3px solid #b8c0d0; padding-left: 11px; }
  .notes-composer.composer-external { border-left: 3px solid #0b8a63; padding-left: 11px; }
  .notes-composer.composer-external .compose-scope-hint { color: #0b6b52; }
  .notes-composer.composer-external .notes-textarea {
    border-color: #b6e6d5;
    background: #f6fdfa;
  }

  .save-btn-external {
    background: #0b8a63;
    border-color: #0b8a63;
  }

  /* ── Directory typeahead dropdown (LO picker + note recipients) ──────────── */
  .lo-picker-group { position: relative; }

  .user-dropdown {
    position: absolute;
    z-index: 20;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 4px;
    max-height: 220px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #dfe4ee;
    border-radius: 6px;
    box-shadow: 0 6px 20px rgba(20, 40, 80, 0.12);
  }

  .user-dropdown-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    width: 100%;
    text-align: left;
    border: none;
    background: transparent;
    padding: 7px 12px;
    cursor: pointer;
  }

  .user-dropdown-row:hover { background: #f2f6fc; }
  .user-dropdown-name { font-size: 13px; font-weight: 600; color: #23314f; }
  .user-dropdown-email { font-size: 11px; color: #7a8399; }

  /* ── Note recipients ─────────────────────────────────────────────────────── */
  .note-recipients { margin-top: 10px; }

  .recipients-label {
    display: block;
    font-size: 12px;
    font-weight: 700;
    color: #3a4a6a;
    margin-bottom: 4px;
  }

  .recipients-box {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    border: 1px solid #dfe4ee;
    border-radius: 6px;
    padding: 6px 8px;
    background: #fcfcfe;
  }

  .recipient-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #eaf1fb;
    border: 1px solid #d3e2f7;
    border-radius: 12px;
    padding: 2px 6px 2px 10px;
    font-size: 12px;
    color: #23406e;
    max-width: 100%;
  }

  .recipient-chip-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }

  .recipient-chip-x {
    border: none;
    background: transparent;
    color: #5c7aa8;
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 2px;
  }

  .recipient-chip-x:hover { color: #d0342c; }

  .recipients-input {
    flex: 1;
    min-width: 180px;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #23314f;
    padding: 3px 2px;
    font-family: inherit;
  }

  .recipients-dropdown { top: calc(100% + 4px); }

  .composer-buttons { display: flex; gap: 8px; align-items: center; }

  .ghost-save-btn {
    background: #fff;
    color: #2f6fed;
    border: 1px solid #bcd2f7;
  }

  .send-note-btn { background: #0b8a63; border-color: #0b8a63; }
  .send-note-btn:disabled { opacity: 0.55; }

  /* ── Not Authorized ──────────────────────────────────────────────────────── */
  .not-authorized-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 10px;
    padding: 60px 20px;
  }

  .not-authorized-icon {
    font-size: 36px;
    color: #b0b9cc;
  }

  .not-authorized-title {
    font-size: 18px;
    font-weight: 700;
    color: #3a4a6a;
  }

  .not-authorized-text {
    font-size: 14px;
    color: #7b8397;
  }

  /* ── AI Assist ───────────────────────────────────────────────────────────── */
  .create-mode-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e7ebf2;
  }

  .create-mode-tab {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    border: 1px solid #dde2eb;
    background: #f7f8fc;
    color: #5a6680;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .create-mode-tab.active {
    background: #eef4ff;
    border-color: #cfd8f5;
    color: #315db5;
  }

  .create-mode-tab:hover:not(.active) { background: #f0f2f8; }

  .ai-assist-section {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .ai-assist-input-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .ai-optional-hint {
    font-size: 11px;
    font-weight: 400;
    color: #94a0b8;
    text-transform: none;
    letter-spacing: 0;
    margin-left: 4px;
  }

  .ai-text-input { resize: vertical; }

  .ai-file-drop {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 14px;
    border: 1px dashed #c8d0e3;
    border-radius: 5px;
    background: #fafbfd;
    color: #7b8397;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .ai-file-drop:hover { border-color: #0b84ff; background: #f5f9ff; color: #315db5; }
  .ai-file-drop.has-file { border-style: solid; border-color: #2c7a4b; background: #f6fdf8; color: #2c7a4b; }

  .ai-file-icon { font-size: 16px; flex-shrink: 0; }
  .ai-file-ok { color: #2c7a4b; }
  .ai-file-hidden { display: none; }

  .ai-assist-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ai-generate-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 18px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 5px;
    border: 1px solid #7c4dff;
    background: #7c4dff;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .ai-generate-btn:hover { background: #6a3de0; border-color: #6a3de0; }
  .ai-generate-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  .ai-generating-hint {
    font-size: 12px;
    color: #7b8397;
    font-style: italic;
  }

  .ai-populated-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    background: #f0f4ff;
    border: 1px solid #cfd8f5;
    border-radius: 5px;
    color: #315db5;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 14px;
  }

  .ai-note-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 14px;
  }

  .ai-note-hint {
    font-size: 11px;
    font-weight: 400;
    color: #7b8397;
    text-transform: none;
    letter-spacing: 0;
    margin-left: 8px;
  }

  /* ── Concession approvers importer ──────────────────────────────────── */
  .approvers-btn {
    height: 38px;
    width: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #7b8397;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }
  .approvers-btn:hover { background: #eef4ff; border-color: #c2d4f0; color: #123a71; }
  .approvers-btn .fa-duotone,
  .approvers-btn .fa-duotone::before,
  .approvers-btn .fa-duotone::after {
    --fa-primary-opacity: 1;
    --fa-secondary-opacity: 1;
  }

  .approver-modal-card { max-width: 1080px; width: 96vw; }
  .approver-modal-body { max-height: 78vh; overflow-y: auto; }

  .approver-current {
    font-size: 13px;
    color: #3c4658;
    background: #f6f8fc;
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }
  .approver-current-sub { color: #7b8397; }

  .approver-drop {
    position: relative;
    border: 2px dashed #d9dfeb;
    border-radius: 8px;
    background: #fbfcfe;
    padding: 22px 16px 18px;
    text-align: center;
    transition: all 0.15s ease;
  }
  .approver-drop.is-over { border-color: #123a71; background: #eef4ff; }
  .approver-drop.has-file { border-style: solid; border-color: #c2d4f0; background: #ffffff; }
  .approver-file-input { display: none; }
  .approver-drop-icon { font-size: 26px; color: #7b8397; margin-bottom: 8px; display: block; }
  .approver-drop.has-file .approver-drop-icon { color: #1e7d4f; }
  .approver-drop-title { font-size: 14px; font-weight: 600; color: #22293a; }
  .approver-drop-hint { font-size: 12px; color: #7b8397; margin-top: 2px; }
  .approver-browse-btn {
    margin-top: 12px;
    height: 32px;
    padding: 0 14px;
    font-size: 13px;
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #3c4658;
    cursor: pointer;
  }
  .approver-browse-btn:hover:not(:disabled) { background: #eef4ff; border-color: #c2d4f0; }
  .approver-browse-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .approver-busy {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #3c4658;
    padding: 14px 0 4px;
  }

  .approver-result-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    border-radius: 6px;
    padding: 10px 12px;
    margin: 14px 0 12px;
  }
  .approver-result-banner.is-preview { background: #eef4ff; border: 1px solid #c2d4f0; color: #123a71; }
  .approver-result-banner.is-committed { background: #e9f7ef; border: 1px solid #b9e3cb; color: #1e7d4f; }

  .approver-warn-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12.5px;
    line-height: 1.5;
    background: #fff8e6;
    border: 1px solid #f0dca8;
    color: #7a5b12;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 14px;
  }

  .approver-counts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
    gap: 8px;
    margin-bottom: 14px;
  }
  .approver-count-card {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    background: #ffffff;
    padding: 10px 8px;
    text-align: center;
  }
  .approver-count-value { font-size: 20px; font-weight: 600; color: #22293a; }
  .approver-count-label {
    font-size: 10.5px;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-top: 2px;
  }
  .approver-count-card.is-new .approver-count-value { color: #1e7d4f; }
  .approver-count-card.is-changed .approver-count-value { color: #123a71; }
  .approver-count-card.is-off .approver-count-value { color: #a4472f; }

  .approver-section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 11px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 16px 0 8px;
  }
  .approver-toggle {
    border: none;
    background: none;
    color: #123a71;
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    padding: 0;
  }
  .approver-toggle:hover { text-decoration: underline; }

  .approver-table-wrap { overflow-x: auto; border: 1px solid #e4e9f2; border-radius: 6px; }
  .approver-table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .approver-table th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 600;
    color: #7b8397;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    background: #f6f8fc;
    padding: 8px 10px;
    border-bottom: 1px solid #e4e9f2;
    white-space: nowrap;
  }
  .approver-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f3f8;
    color: #3c4658;
  }
  .approver-table tbody tr:last-child td { border-bottom: none; }
  .approver-table th.num, .approver-table td.num { text-align: right; }
  .approver-table td.is-zero { color: #a4472f; font-weight: 600; }

  .approver-pill {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 10px;
    padding: 2px 8px;
  }
  .approver-pill.is-yes { background: #e9f7ef; color: #1e7d4f; }
  .approver-pill.is-no { background: #f1f2f5; color: #7b8397; }

  .approver-table-note {
    font-size: 11.5px;
    line-height: 1.5;
    color: #7b8397;
    margin-top: 6px;
  }

  .approver-warn-list, .approver-change-list {
    border: 1px solid #e4e9f2;
    border-radius: 6px;
    max-height: 260px;
    overflow-y: auto;
  }
  .approver-warn-row {
    display: grid;
    grid-template-columns: 64px minmax(140px, 200px) 1fr;
    gap: 10px;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-warn-row:last-child { border-bottom: none; }
  .approver-warn-row-loc { color: #7b8397; }
  .approver-warn-row-who { color: #3c4658; overflow-wrap: anywhere; }
  .approver-warn-row-msg { color: #7a5b12; }
  .approver-warn-more {
    font-size: 11.5px;
    color: #7b8397;
    padding: 8px 10px;
    background: #f6f8fc;
  }

  .approver-change-row {
    display: grid;
    grid-template-columns: 92px minmax(160px, 220px) 120px 1fr;
    gap: 10px;
    align-items: start;
    font-size: 12px;
    padding: 7px 10px;
    border-bottom: 1px solid #f0f3f8;
  }
  .approver-change-row:last-child { border-bottom: none; }
  .approver-change-tag {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 10px;
    padding: 2px 7px;
    text-align: center;
  }
  .approver-change-tag.tag-new { background: #e9f7ef; color: #1e7d4f; }
  .approver-change-tag.tag-changed { background: #eef4ff; color: #123a71; }
  .approver-change-tag.tag-deactivating { background: #fdeeea; color: #a4472f; }
  .approver-change-tag.tag-reactivating { background: #fff8e6; color: #7a5b12; }
  .approver-change-who { color: #22293a; display: flex; flex-direction: column; overflow-wrap: anywhere; }
  .approver-change-email { font-size: 11px; color: #7b8397; }
  .approver-change-div { color: #7b8397; }
  .approver-change-fields { display: flex; flex-direction: column; gap: 2px; }
  .approver-change-field { color: #3c4658; overflow-wrap: anywhere; }
  .approver-change-field em { color: #7b8397; font-style: normal; }
</style>
